import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { format } from 'date-fns';
import { unparse } from 'papaparse';
import { FormProvider, useForm } from 'react-hook-form';
import { ReactComponent as DeleteIcon } from 'src/icons/delete.svg';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { getIconForMetric } from 'src/utils';
import { formatDateForDisplay, normalizeDateString } from 'src/utils/dateUtils';
import { CREATE_CUSTOM_REPORT } from 'src/utils/gql/mutations';
import {
  GET_CUSTOM_FORMULA_VALUE,
  GET_CUSTOM_FORMULAS,
  GET_FACEBOOK_METRICS_VALUES,
  GET_GOOGLE_ADS_METRICS_DRAFT,
  GET_GOOGLE_ANALYTICS_4_METRICS,
  GET_GOOGLE_SEARCH_CONSOLE_METRIC_VALUES,
  GET_SHOPIFY_METRICS_VALUES,
  GET_MAGENTO_METRICS_VALUES,
  GET_DATABASE_DATA_FOR_CUSTOM_REPORT,
  GET_KIPIFY_METRICS_VALUES,
} from 'src/utils/gql/queries';
import {
  ConnectorType,
  DataSourceConfig,
  GetCustomFormulaArgs,
  GetFacebookMetricValuesVariables,
  GetGoogleAdsMetricsVariables,
  GetGoogleAnalytics4MetricVariables,
  GetGoogleSearchConsoleMetricValuesVariables,
  GetShopifyMetricValuesVariables,
  GetKipifyMetricValuesVariables,
  Metric,
  MetricsByConnectorType,
  MetricValue,
  MetricValuesRow,
  MetricValuesRowsGroupedByConnector,
  Mutation,
  MutationCreateCustomReportArgs,
  Query,
  ValueType,
  FilterValueType,
  Split,
  MetricFormatType,
} from 'src/utils/gql/types';
import { FrontendFilterType, FilterWithValue } from './types';

import { useConnectors } from '../../providers/connectors/hook/useConnectors';
import { formatCustomReportCellValue } from '../DatabaseConnector/formatDatabaseConnectorMetricValue';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useUnifiedSearchTermsIntegration } from '../../hooks/useUnifiedSearchTermsIntegration';

import { DashboardHeader } from './components/DashboardHeader';
import { FiltersBlock } from './components/FiltersBlock';
import { MetricsContent } from './components/MetricsContent';
import { CustomTable } from './components/Table';
import { Wrapper as NoDataWrapper } from './components/Table/styled';
import TableLoader from './components/TableLoader';
import { notApplicableValue, googleAdsCitiesMap } from './constants';
import {
  DeleteButton,
  DownloadButton,
  MainButtonGroupWrapper,
  TableWrapper,
  Wrapper,
} from './styled';
import {
  DashboardForm,
  DashboardTableProps,
  InputName,
  TableRowsType,
} from './types';
import { findCompareValue, calculateChange, normalizeVendorBrandValue } from './compareUtils';
import {
  getFiltersByType,
  getMetricsKeys,
  getMetricsLabels,
  getSplitsKeys,
  getSplitsLabels,
  getTitle,
  sortBySplitsAndMetrics,
  buildColumnId,
  cellsMatchColumnId,
  groupRowsByCountry,
  groupRowsByKipifyCountry,
  extractCountryCodeFromRegion,
  joinSplitNormalizedKey,
  splitKeysEquivalent,
  googleAdsSplitCatalogMatchesRowKey,
  googleAdsMetricCatalogMatchesRowKey,
} from './utils';

// IMPORTANTE: Il backend gestisce il timezone dello shop, quindi inviamo solo YYYY-MM-DD
// Il backend convertirà automaticamente in UTC usando il timezone dello shop
// Non aggiungiamo T00:00:00.000Z o T23:59:59.999Z perché il backend lo fa per noi
const adjustStartDate = (date: string): string => {
  // Estrai solo YYYY-MM-DD, il backend farà la conversione timezone
  const dateOnly = date.split('T')[0];
  return dateOnly || date;
};

const adjustEndDate = (date: string): string => {
  // Estrai solo YYYY-MM-DD, il backend farà la conversione timezone
  const dateOnly = date.split('T')[0];
  return dateOnly || date;
};

/**
 * Data table for custom reports.
 * - Displays selected metrics and splits.
 * - Allows saving the report (with current config) via handleSaveReport.
 * - After saving, the report list is updated via parent refetch.
 * - Responsive layout: table never goes below sidebar, always horizontally scrollable on mobile.
 * - "Save report" and "Download" buttons are always visible if table is valid.
 * - Delete button closes table and returns to list.
 */
export const DashboardTable = ({
  onCloseTable,
  title,
  initData,
  onConfigChange,
  onTitleChange,
  titleInputRef,
  onTitleSave,
  showTitleSaved,
  hideSaveAndDeleteControls,
  columnOrder: propColumnOrder,
  changeColumnOrder: propChangeColumnOrder,
  // Props opzionali per i connettori
  googleAdsConnectorId: propGoogleAdsConnectorId,
  shopifyConnectorId: propShopifyConnectorId,
  facebookConnectorId: propFacebookConnectorId,
  googleAnalytics4ConnectorId: propGoogleAnalytics4ConnectorId,
  clientAccountId: propClientAccountId,
  propertyId: propPropertyId,
  compareDateRange,
}: DashboardTableProps) => {
  const snackbar = useSnackbar();

  // Hook per ottenere i connettori (usato solo se le props non sono fornite)
  const {
    googleAdsConnectorId: hookGoogleAdsConnectorId,
    shopifyConnectorId: hookShopifyConnectorId,
    facebookConnectorId: hookFacebookConnectorId,
    googleAnalytics4ConnectorId: hookGoogleAnalytics4ConnectorId,
    googleSearchConsoleConnectorId,
    searchConsoleSiteUrl,
    clientAccountId: hookClientAccountId,
    propertyId: hookPropertyId,
    databaseConnectorId,
    magentoConnectorId,
    magentoConnectorEnabled,
  } = useConnectors();

  // Usa le props se fornite, altrimenti usa i valori del hook
  const googleAdsConnectorId =
    propGoogleAdsConnectorId || hookGoogleAdsConnectorId;
  const shopifyConnectorId = propShopifyConnectorId || hookShopifyConnectorId;
  const facebookConnectorId =
    propFacebookConnectorId || hookFacebookConnectorId;
  const googleAnalytics4ConnectorId =
    propGoogleAnalytics4ConnectorId || hookGoogleAnalytics4ConnectorId;
  const clientAccountId = propClientAccountId || hookClientAccountId;
  const propertyId = propPropertyId || hookPropertyId;

  const [expandedMetricCollapseType, setExpandedMetricCollapseType] = useState<
    (ConnectorType | MetricsByConnectorType)[]
  >([]);
  const [metricsIsShown, setMetricsIsShown] = useState<boolean>(false);

  const [tableColumnOrder, setTableColumnOrder] = useState<string[]>([]);
  const [orderInitialized, setOrderInitialized] = useState(false); // Flag per tracciare se l'ordine è stato inizializzato da initData/prop
  const [tableSortConfig, setTableSortConfig] = useState<{
    columnId: string | null;
    direction: 'asc' | 'desc';
  }>({ columnId: null, direction: 'desc' });

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Inizializza tableColumnOrder da initData.columnOrder o dalla prop columnOrder quando disponibile
  // IMPORTANTE: non sovrascrivere se l'utente ha già modificato l'ordinamento
  useEffect(() => {
    // Solo se l'ordine non è stato ancora inizializzato
    if (!orderInitialized) {
      // Priorità: prop columnOrder > initData.columnOrder
      if (
        propColumnOrder &&
        Array.isArray(propColumnOrder) &&
        propColumnOrder.length > 0
      ) {
        setTableColumnOrder(propColumnOrder);
        setOrderInitialized(true);
      } else if (
        initData?.columnOrder &&
        Array.isArray(initData.columnOrder) &&
        initData.columnOrder.length > 0
      ) {
        setTableColumnOrder(initData.columnOrder);
        setOrderInitialized(true);
      } else if (tableColumnOrder.length === 0) {
        // Se non c'è un ordine salvato e tableColumnOrder è vuoto, mantieni vuoto
        setOrderInitialized(true);
      }
    }
  }, [initData?.columnOrder, propColumnOrder, orderInitialized]);

  // Inizializza sortConfig da initData quando disponibile (Custom Report: serve a non "perdere" il sort su refresh)
  useEffect(() => {
    if (initData?.sortConfig) {
      setTableSortConfig(initData.sortConfig);
    }
  }, [initData?.sortConfig]);

  // Sincronizza tableColumnOrder con la prop changeColumnOrder quando cambia
  const handleColumnOrderChange = (newOrder: string[]) => {
    setTableColumnOrder(newOrder);
    if (propChangeColumnOrder) {
      propChangeColumnOrder(newOrder);
    }
  };

  useEffect(() => {
    if (isSmallScreen && metricsIsShown) {
      setExpandedMetricCollapseType([]);
      setMetricsIsShown(false);
    }
  }, [isSmallScreen, metricsIsShown]);

  const openOrCloseAllMetrics = (isOpen: boolean) => {
    setExpandedMetricCollapseType(!isOpen ? [] : Object.values(ConnectorType));
  };

  const toggleMetricCollapse = (
    type: ConnectorType | MetricsByConnectorType,
    isSearch?: boolean,
  ) => {
    setExpandedMetricCollapseType((prevType) =>
      prevType?.includes(type)
        ? prevType.filter((colType) => colType !== type)
        : isSearch
          ? [...prevType, type]
          : [type],
    );
  };

  const handleCloseMetricsClick = () => {
    setExpandedMetricCollapseType([]);
    setMetricsIsShown(false);
  };

  const onError = (err: ApolloError) => {
    const { graphQLErrors, message: errorText } = err;
    const message =
      graphQLErrors && graphQLErrors.length > 0
        ? graphQLErrors[0].message
        : errorText;

    snackbar(message);
  };

  const {
    accountsLoading,
    analyticsAccountLoading,
    shopifyMetrics,
    magentoMetrics,
    facebookMetrics,
    googleAdsMetrics,
    googleAnalyticsMetrics,
    googleSearchConsoleMetrics,
    databaseMetrics,
    gettingFacebookMetrics,
    gettingShopifyMetrics,
    gettingMagentoMetrics,
    gettingGoogleAdsMetrics,
    gettingGoogleAnalyticsMetrics,
    gettingGoogleSearchConsoleMetrics,
    splits,
  } = useConnectors();

  // const { data: shopifyFiltersData } = useQuery<Pick<Query, 'getShopifyFilters'>>(GET_SHOPIFY_FILTERS, {
  //   onError,
  // });

  const defaultValues = useMemo<DashboardForm>(() => {
    if (!initData) {
      return {
        startDate: format(new Date(new Date().setUTCDate(1)), 'yyyy-MM-dd'),
        endDate: format(new Date(), 'yyyy-MM-dd'),
        splits: [],
        metrics: [],
        filters: [],
      };
    }

    // Usa sempre le date salvate in initData.dateRange se presenti, altrimenti fallback su oggi
    // IMPORTANTE: estrai solo YYYY-MM-DD per evitare problemi di timezone
    const safeDateParse = (dateStr: string | undefined | null): Date => {
      if (!dateStr || typeof dateStr !== 'string' || dateStr.trim() === '') {
        return new Date();
      }
      // Estrai solo la parte YYYY-MM-DD per evitare problemi di timezone
      const dateOnly = dateStr.split('T')[0];
      const match = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (match) {
        const [, yearStr, monthStr, dayStr] = match;
        const year = parseInt(yearStr, 10);
        const month = parseInt(monthStr, 10);
        const day = parseInt(dayStr, 10);
        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
          // Crea una data locale (non UTC) per evitare problemi di timezone
          return new Date(year, month - 1, day);
        }
      }
      // Fallback: prova con new Date() se non riusciamo a estrarre la data
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? new Date() : date;
    };

    const fromDateObj = initData?.dateRange?.from
      ? safeDateParse(initData.dateRange.from)
      : new Date(new Date().setUTCDate(1));
    const toDateObj = initData?.dateRange?.to
      ? safeDateParse(initData.dateRange.to)
      : new Date();

    // Estrai solo YYYY-MM-DD per evitare problemi di timezone
    const startDate = `${fromDateObj.getFullYear()}-${String(fromDateObj.getMonth() + 1).padStart(2, '0')}-${String(fromDateObj.getDate()).padStart(2, '0')}`;
    const endDate = `${toDateObj.getFullYear()}-${String(toDateObj.getMonth() + 1).padStart(2, '0')}-${String(toDateObj.getDate()).padStart(2, '0')}`;

    const getActiveMetricsFromRows = (
      availableMetrics: Array<Metric>,
      connectorType: ConnectorType,
      rows?: MetricValuesRow[],
    ): Array<Metric> => {
      if (!rows?.length) {
        return [];
      }

      return availableMetrics.filter((currentMetric) =>
        rows.some((row) =>
          row.metricValues.some((activeMetric) => {
            if (connectorType === ConnectorType.GOOGLE_ADS) {
              return googleAdsMetricCatalogMatchesRowKey(
                currentMetric.key,
                activeMetric.key,
              );
            }
            return activeMetric.key === currentMetric.key;
          }),
        ),
      );
    };

    // Validazione sicura per initData.rows
    const rows = initData?.rows || {};
    const { shopify, facebook, googleAds, googleAnalytics4, database, kipify } =
      rows;

    const facebookActiveMetrics = getActiveMetricsFromRows(
      facebookMetrics,
      ConnectorType.FACEBOOK,
      facebook,
    );
    const shopifyActiveMetrics = getActiveMetricsFromRows(
      shopifyMetrics,
      ConnectorType.SHOPIFY,
      shopify,
    );
    const googleAdsActiveMetrics = getActiveMetricsFromRows(
      googleAdsMetrics,
      ConnectorType.GOOGLE_ADS,
      googleAds,
    );
    const googleAnalyticsActiveMetrics = getActiveMetricsFromRows(
      googleAnalyticsMetrics,
      ConnectorType.GOOGlE_ANALYTICS4,
      googleAnalytics4,
    );
    const databaseActiveMetrics = getActiveMetricsFromRows(
      databaseMetrics as unknown as Metric[],
      ConnectorType.DATABASE,
      database,
    );

    // --- CORREZIONE: Estrazione splits da multiple fonti ---
    // 1. Estrai splits salvati direttamente nel config (per report salvati)
    const configSplits: Split[] = Array.isArray((initData as any)?.splits)
      ? (initData as any).splits
      : [];

    // 2. Estrai splits dagli args del messaggio (per conversazioni)
    const args = (initData as any)?.args || {};
    const argsSplits: Split[] = [];

    Object.entries(args).forEach(
      ([connectorType, connectorArgs]: [string, any]) => {
        if (connectorArgs?.splits && Array.isArray(connectorArgs.splits)) {
          connectorArgs.splits.forEach((splitKey: string) => {
            const foundSplit = splits.find(
              (split) =>
                split.key === splitKey && split.connectorType === connectorType,
            );
            if (foundSplit && !argsSplits.some((s) => s.id === foundSplit.id)) {
              argsSplits.push(foundSplit);
            }
          });
        }
      },
    );

    // 3. Estrai splits dalle rows per completezza
    const activeSplitKeysMap = Object.entries(rows).reduce<
      Partial<Record<keyof MetricValuesRowsGroupedByConnector, Set<string>>>
    >((acc, [type, rows]) => {
      if (rows?.length) {
        const currentKeySet = new Set<string>();

        for (const { splitValues } of rows) {
          if (!splitValues?.length) {
            continue;
          }

          for (const split of splitValues) {
            if (split?.key) {
              currentKeySet.add(split.key);
            }
          }
        }

        acc[type as keyof MetricValuesRowsGroupedByConnector] = currentKeySet;
      }

      return acc;
    }, {});

    const rowsSplits = splits.filter(({ key, connectorType }) => {
      const set = activeSplitKeysMap[connectorType];
      if (!set?.size) return false;
      if (set.has(key)) return true;
      if (connectorType === ConnectorType.GOOGLE_ADS) {
        const rowKeys = Array.from(set);
        for (let i = 0; i < rowKeys.length; i++) {
          if (googleAdsSplitCatalogMatchesRowKey(key, rowKeys[i])) return true;
        }
      }
      return false;
    });

    // Combina splits unici da tutte le fonti (config ha priorità)
    const allSplits = [...configSplits];
    argsSplits.forEach((split) => {
      if (!allSplits.some((s) => s.id === split.id)) {
        allSplits.push(split);
      }
    });
    rowsSplits.forEach((split) => {
      if (!allSplits.some((s) => s.id === split.id)) {
        allSplits.push(split);
      }
    });
    // --- FINE CORREZIONE SPLITS ---

    // --- CORREZIONE: Estrazione metrics e filters dal config salvato ---
    // Estrai metrics salvate direttamente nel config (per report salvati)
    const configMetrics: Metric[] = Array.isArray((initData as any)?.metrics)
      ? (initData as any).metrics
      : [];

    // Estrai filters salvati direttamente nel config (per report salvati)
    const configFilters: FilterWithValue[] = Array.isArray(
      (initData as any)?.filters,
    )
      ? (initData as any).filters
      : [];

    // Combina metrics da config con quelle dalle rows (config ha priorità)
    const allMetrics = [...configMetrics];
    [
      ...facebookActiveMetrics,
      ...shopifyActiveMetrics,
      ...googleAdsActiveMetrics,
      ...googleAnalyticsActiveMetrics,
      ...databaseActiveMetrics,
    ].forEach((metric) => {
      if (!allMetrics.some((m) => m.id === metric.id)) {
        allMetrics.push(metric);
      }
    });

    // --- CORREZIONE: Estrazione filtri dagli args del messaggio ---
    // Funzione di utilità per costruire un FilterWithValue per Customer Type
    const buildCustomerTypeFilter = (value: string): FilterWithValue => ({
      id: 'customer_type',
      key: 'customer_type',
      label: 'Customer Type',
      type: FrontendFilterType.SimpleFilter,
      connectorType: ConnectorType.SHOPIFY,
      operators: [
        { key: 'IN', label: 'Is' },
        { key: 'NOT_IN', label: 'Is Not' },
        { key: 'CONTAIN', label: 'Contains' },
        { key: 'NOT_CONTAIN', label: 'Does Not Contain' },
      ],
      expectedReturnValueType: FilterValueType.ArrayStr,
      shouldAvailableValuesBeRequested: false,
      availableValues: [
        { key: 'First time', label: 'First time' },
        { key: 'Recurring', label: 'Recurring' },
      ],
      values: [
        {
          operator: 'IN',
          values: [value],
        },
      ],
    });

    // Funzione generica per mappare qualsiasi filtro da initData a FilterWithValue
    const mapFilterToFilterWithValue = (
      filter: any,
    ): FilterWithValue | null => {
      if (
        !filter?.key ||
        !filter?.values ||
        !Array.isArray(filter.values) ||
        filter.values.length === 0
      ) {
        return null;
      }
      // Escludi filtri con valori vuoti o undefined
      const filteredValues = filter.values.filter(
        (v: any) => v !== undefined && v !== null && v !== '',
      );
      if (filteredValues.length === 0) {
        return null;
      }
      // Operatore valido
      const operator =
        typeof filter.operator === 'string' && filter.operator.length > 0
          ? filter.operator
          : 'IN';
      // ... resto invariato ...
      const knownFilters: Record<string, Partial<FilterWithValue>> = {
        customer_type: {
          id: 'customer_type',
          key: 'customer_type',
          label: 'Customer Type',
          type: FrontendFilterType.SimpleFilter,
          connectorType: ConnectorType.SHOPIFY,
          operators: [
            { key: 'IN', label: 'Is' },
            { key: 'NOT_IN', label: 'Is Not' },
            { key: 'CONTAIN', label: 'Contains' },
            { key: 'NOT_CONTAIN', label: 'Does Not Contain' },
          ],
          expectedReturnValueType: FilterValueType.ArrayStr,
          shouldAvailableValuesBeRequested: false,
          availableValues: [
            { key: 'First time', label: 'First time' },
            { key: 'Recurring', label: 'Recurring' },
          ],
        },
      };
      const knownFilter = knownFilters[filter.key];
      if (knownFilter) {
        return {
          ...knownFilter,
          values: [
            {
              operator,
              values: filteredValues,
            },
          ],
        } as FilterWithValue;
      }
      return {
        id: filter.key,
        key: filter.key,
        label: filter.key
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (l: string) => l.toUpperCase()),
        type: FrontendFilterType.SimpleFilter,
        connectorType: ConnectorType.SHOPIFY,
        operators: [
          { key: 'IN', label: 'Is' },
          { key: 'NOT_IN', label: 'Is Not' },
          { key: 'CONTAIN', label: 'Contains' },
          { key: 'NOT_CONTAIN', label: 'Does Not Contain' },
        ],
        expectedReturnValueType: FilterValueType.ArrayStr,
        shouldAvailableValuesBeRequested: true,
        availableValues: [],
        values: [
          {
            operator,
            values: filteredValues,
          },
        ],
      };
    };

    // Estrai filtri da multiple fonti per completezza
    let filters: FilterWithValue[] = [];

    // 1. Estrai filtri da initData.table.filters o initData.filters
    const possibleFilters =
      (initData as any)?.filters || (initData as any)?.table?.filters || [];
    possibleFilters.forEach((filter: any) => {
      const mappedFilter = mapFilterToFilterWithValue(filter);
      if (mappedFilter) {
        filters.push(mappedFilter);
      }
    });

    // 2. Estrai filtri dagli args del messaggio
    Object.entries(args).forEach(
      ([connectorType, connectorArgs]: [string, any]) => {
        if (connectorArgs?.filters && Array.isArray(connectorArgs.filters)) {
          connectorArgs.filters.forEach((filter: any) => {
            const mappedFilter = mapFilterToFilterWithValue(filter);
            if (
              mappedFilter &&
              !filters.some((f) => f.id === mappedFilter.id)
            ) {
              filters.push(mappedFilter);
            }
          });
        }
      },
    );

    // Filtro finale: solo filtri con almeno un valore valido (sia semplice che con filtersGroup)
    filters = filters.filter(
      (f) =>
        Array.isArray(f.values) &&
        f.values.length > 0 &&
        (('values' in f.values[0] &&
          Array.isArray((f.values[0] as any).values) &&
          (f.values[0] as any).values.length > 0) ||
          ('filtersGroup' in f.values[0] &&
            Array.isArray((f.values[0] as any).filtersGroup) &&
            (f.values[0] as any).filtersGroup.length > 0)),
    );
    // --- FINE CORREZIONE FILTRI ---

    return {
      startDate,
      endDate,
      metrics: allMetrics,
      splits: allSplits,
      filters: configFilters.length > 0 ? configFilters : filters,
    };
  }, [
    facebookMetrics,
    googleAdsMetrics,
    googleAnalyticsMetrics,
    initData,
    shopifyMetrics,
    splits,
    databaseMetrics,
  ]);

  const methods = useForm<DashboardForm>({
    mode: 'onChange',
    defaultValues,
  });

  // Auto-population: when opening from chat, use metrics/splits present in initData.rows
  // Only on first load, not when user has already interacted with the form
  useEffect(() => {
    if (!initData || !initData.rows) return;

    const currentMetrics = methods.getValues('metrics') || [];
    const currentSplits = methods.getValues('splits') || [];
    const isFormDirty = methods.formState.isDirty;

    // Don't automatically restore if user has already interacted with the form
    // or if user has explicitly deselected all splits
    if (isFormDirty || currentSplits.length === 0) return;

    const rows = initData.rows as Partial<MetricValuesRowsGroupedByConnector>;

    // Helper to extract keys from rows
    const extractMetricKeys = (rowsArr?: MetricValuesRow[]) => {
      const set = new Set<string>();
      rowsArr?.forEach((r) =>
        r.metricValues?.forEach((m) => m?.key && set.add(m.key)),
      );
      return Array.from(set);
    };
    const extractSplitKeys = (rowsArr?: MetricValuesRow[]) => {
      const set = new Set<string>();
      rowsArr?.forEach((r) =>
        r.splitValues?.forEach((s) => s?.key && set.add(s.key)),
      );
      return Array.from(set);
    };

    // Se non ci sono metrics selezionate, ricostruiscile dalle rows usando i cataloghi disponibili del provider
    if (!currentMetrics.length) {
      const collected: Metric[] = [];
      const addFrom = (available: Metric[], keys: string[]) => {
        if (!Array.isArray(available) || !available.length) return;
        available.forEach((m) => {
          if (keys.includes(m.key) && !collected.some((x) => x.id === m.id)) {
            collected.push(m);
          }
        });
      };

      addFrom(facebookMetrics, extractMetricKeys(rows.facebook));
      addFrom(shopifyMetrics, extractMetricKeys(rows.shopify));
      addFrom(googleAdsMetrics, extractMetricKeys(rows.googleAds));
      addFrom(googleAnalyticsMetrics, extractMetricKeys(rows.googleAnalytics4));

      if (collected.length) {
        methods.setValue('metrics', collected, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }

    // Se non ci sono splits selezionati, ricostruiscili dalle rows usando gli splits globali disponibili
    if (!currentSplits.length) {
      const splitKeys = new Set<string>();
      const addSplitKeys = (arr?: MetricValuesRow[]) =>
        extractSplitKeys(arr).forEach((k) => splitKeys.add(k));
      addSplitKeys(rows.facebook);
      addSplitKeys(rows.shopify);
      addSplitKeys(rows.googleAds);
      addSplitKeys(rows.googleAnalytics4);

      if (splitKeys.size) {
        const availableSplits = splits || [];
        const selected = availableSplits.filter((s) => splitKeys.has(s.key));
        if (selected.length) {
          methods.setValue('splits', selected as any, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      }
    }
  }, [
    initData,
    facebookMetrics,
    shopifyMetrics,
    googleAdsMetrics,
    googleAnalyticsMetrics,
    splits,
    methods,
  ]);

  // Debounce dei valori per evitare chiamate duplicate
  const debouncedMetrics = useDebouncedValue(methods.watch('metrics'), 300);
  const debouncedSplits = useDebouncedValue(methods.watch('splits'), 300);
  const debouncedFilters = useDebouncedValue(methods.watch('filters'), 300);
  const debouncedStartDate = useDebouncedValue(methods.watch('startDate'), 300);
  const debouncedEndDate = useDebouncedValue(methods.watch('endDate'), 300);

  const { data: customFormulasData, loading: customFormulasLoading } =
    useQuery<Pick<Query, 'getAllCustomFormulas'>>(GET_CUSTOM_FORMULAS);

  // Funzione di mapping tra MetricFormatType e ValueType
  const mapFormatTypeToValueType = (
    formatType: MetricFormatType,
  ): ValueType => {
    switch (formatType) {
      case MetricFormatType.Currency:
        return ValueType.currency;
      case MetricFormatType.Percent:
        return ValueType.percentage;
      case MetricFormatType.Duration:
        return ValueType.duration;
      case MetricFormatType.Number:
      default:
        return ValueType.number;
    }
  };

  const transformedCustomFormulaData =
    customFormulasData?.getAllCustomFormulas?.map((el) => {
      return {
        id: el.id,
        label: el.name,
        key: el.id,
        category: 'metric',
        type: MetricsByConnectorType.CUSTOM_FORMULA,
        valueType: mapFormatTypeToValueType(el.format.type),
      };
    });

  const toComparableDateOnly = (raw: string): number | null => {
    const dateOnly = adjustStartDate(String(raw || ''));
    const match = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    const [, y, m, d] = match;
    const year = parseInt(y, 10);
    const month = parseInt(m, 10);
    const day = parseInt(d, 10);
    if (
      Number.isNaN(year) ||
      Number.isNaN(month) ||
      Number.isNaN(day) ||
      year < 2000 ||
      year > 2100 ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31
    ) {
      return null;
    }
    return year * 10000 + month * 100 + day;
  };

  const dateCondition = (() => {
    const start = toComparableDateOnly(debouncedStartDate);
    const end = toComparableDateOnly(debouncedEndDate);
    return start !== null && end !== null && end >= start;
  })();

  const compareDateCondition = (() => {
    if (!compareDateRange) return false;
    const start = toComparableDateOnly(compareDateRange.from);
    const end = toComparableDateOnly(compareDateRange.to);
    return start !== null && end !== null && end >= start;
  })();

  // Query dati Database per Custom Report
  // Estrai metriche e split DB direttamente dalle righe passate dalla chat, come fallback
  const dbMetricsFromInitRows = useMemo(() => {
    const set = new Set<string>();
    (initData?.rows?.database || []).forEach((r: any) => {
      (r?.metricValues || []).forEach((m: any) => m?.key && set.add(m.key));
    });
    return Array.from(set);
  }, [initData]);

  const dbSplitsFromInitRows = useMemo(() => {
    const set = new Set<string>();
    (initData?.rows?.database || []).forEach((r: any) => {
      (r?.splitValues || []).forEach((s: any) => s?.key && set.add(s.key));
    });
    return Array.from(set);
  }, [initData]);

  const gaMetricsFromInitRows = useMemo(() => {
    const set = new Set<string>();
    (initData?.rows?.googleAds || []).forEach((r: any) => {
      (r?.metricValues || []).forEach((m: any) => m?.key && set.add(m.key));
    });
    return Array.from(set);
  }, [initData]);

  const gaSplitsFromInitRows = useMemo(() => {
    const set = new Set<string>();
    (initData?.rows?.googleAds || []).forEach((r: any) => {
      (r?.splitValues || []).forEach((s: any) => s?.key && set.add(s.key));
    });
    return Array.from(set);
  }, [initData]);

  const selectedDbMetricKeys = useMemo(
    () => getMetricsKeys(debouncedMetrics, ConnectorType.DATABASE),
    [debouncedMetrics],
  );
  const selectedDbSplitKeys = useMemo(
    () => getSplitsKeys(debouncedSplits, ConnectorType.DATABASE),
    [debouncedSplits],
  );

  const databaseMetricKeySet = useMemo(
    () => new Set((databaseMetrics || []).map((metric: Metric) => metric.key)),
    [databaseMetrics],
  );

  const promotedDbMetricKeys = useMemo(
    () => selectedDbSplitKeys.filter((key) => databaseMetricKeySet.has(key)),
    [selectedDbSplitKeys, databaseMetricKeySet],
  );

  const effectiveDbMetricKeys = selectedDbMetricKeys.length
    ? selectedDbMetricKeys
    : promotedDbMetricKeys.length
      ? promotedDbMetricKeys
      : dbMetricsFromInitRows;

  const effectiveDbSplitKeys = (
    selectedDbSplitKeys.length ? selectedDbSplitKeys : dbSplitsFromInitRows
  ).filter((key) => !effectiveDbMetricKeys.includes(key));

  const selectedGaMetricKeys = useMemo(
    () => getMetricsKeys(debouncedMetrics, ConnectorType.GOOGLE_ADS),
    [debouncedMetrics],
  );
  const selectedGaSplitKeys = useMemo(
    () => getSplitsKeys(debouncedSplits, ConnectorType.GOOGLE_ADS),
    [debouncedSplits],
  );

  const effectiveGoogleAdsMetricKeys =
    selectedGaMetricKeys.length > 0
      ? selectedGaMetricKeys
      : gaMetricsFromInitRows;
  const effectiveGoogleAdsSplitKeys =
    selectedGaSplitKeys.length > 0 ? selectedGaSplitKeys : gaSplitsFromInitRows;

  const { data: databaseMetricValues, loading: databaseLoading } = useQuery(
    GET_DATABASE_DATA_FOR_CUSTOM_REPORT,
    {
      skip:
        !databaseConnectorId ||
        !dateCondition ||
        (effectiveDbMetricKeys.length === 0 &&
          effectiveDbSplitKeys.length === 0),
      onError,
      variables: {
        connectorId: databaseConnectorId || '',
        splits: effectiveDbSplitKeys,
        metrics: effectiveDbMetricKeys,
        // Serializza i filtri DB in stringhe JSON (schema backend: [String])
        filters: getFiltersByType(debouncedFilters, ConnectorType.DATABASE).map(
          (f) => JSON.stringify(f),
        ),
        dateRange: {
          start: adjustStartDate(debouncedStartDate),
          end: adjustEndDate(debouncedEndDate),
        },
      },
      fetchPolicy: 'network-only',
    },
  );

  // Query di comparazione per Database (stesso path KPICard: start/end, non from/to)
  const { data: compareDatabaseMetricValues } = useQuery(
    GET_DATABASE_DATA_FOR_CUSTOM_REPORT,
    {
      skip:
        !compareDateRange ||
        !compareDateCondition ||
        !databaseConnectorId ||
        !dateCondition ||
        (effectiveDbMetricKeys.length === 0 &&
          effectiveDbSplitKeys.length === 0),
      onError,
      variables: {
        connectorId: databaseConnectorId || '',
        splits: effectiveDbSplitKeys,
        metrics: effectiveDbMetricKeys,
        filters: getFiltersByType(debouncedFilters, ConnectorType.DATABASE).map(
          (f) => JSON.stringify(f),
        ),
        dateRange: compareDateRange
          ? {
              start: adjustStartDate(compareDateRange.from),
              end: adjustEndDate(compareDateRange.to),
            }
          : {
              start: adjustStartDate(debouncedStartDate),
              end: adjustEndDate(debouncedEndDate),
            },
      },
      fetchPolicy: 'network-only',
    },
  );

  const {
    data: googleAds,
    loading: googleAdsLoading,
    refetch: refetchAdsData,
  } = useQuery<
    Pick<Query, 'getGoogleAdsMetrics'>,
    GetGoogleAdsMetricsVariables
  >(GET_GOOGLE_ADS_METRICS_DRAFT, {
    skip:
      !effectiveGoogleAdsMetricKeys.length ||
      !googleAdsConnectorId ||
      !clientAccountId ||
      !dateCondition,
    onError,
    variables: {
      args: {
        metricKeys: effectiveGoogleAdsMetricKeys,
        // Mirroring automatico dello split Country su Google Ads quando selezionato su Shopify/Facebook
        // e quando le metriche riguardano spesa/costo (marketing spend).
        splitsKeys: (() => {
          const base =
            effectiveGoogleAdsSplitKeys.length > 0
              ? effectiveGoogleAdsSplitKeys
              : getSplitsKeys(debouncedSplits, ConnectorType.GOOGLE_ADS);
          if (base.includes('user_country')) return base;

          const shopifySplits = getSplitsKeys(
            debouncedSplits,
            ConnectorType.SHOPIFY,
          );
          const shopifyHasCountry =
            shopifySplits.includes('shipping_country') ||
            shopifySplits.includes('billing_country') ||
            shopifySplits.includes('country');

          const fbHasCountry = getSplitsKeys(
            debouncedSplits,
            ConnectorType.FACEBOOK,
          ).includes('target_country');

          const googleAdsHasSpendMetric = effectiveGoogleAdsMetricKeys.some(
            (k) => /cost|spend/i.test(k),
          );
          const fbHasSpendMetric = getMetricsKeys(
            debouncedMetrics,
            ConnectorType.FACEBOOK,
          ).some((k) => /spend|amount/i.test(k));

          const isMarketingSpendByCountry =
            (shopifyHasCountry || fbHasCountry) &&
            (googleAdsHasSpendMetric || fbHasSpendMetric);

          return isMarketingSpendByCountry ? [...base, 'user_country'] : base;
        })(),
        filters: getFiltersByType(debouncedFilters, ConnectorType.GOOGLE_ADS),
        dateRange: {
          from: adjustStartDate(debouncedStartDate),
          to: adjustEndDate(debouncedEndDate),
        },
      },
      clientAccountId: clientAccountId!,
    },
  });

  const {
    data: shopifyMetricValues,
    loading: shopifyLoading,
    refetch: refetchShopifyData,
  } = useQuery<
    Pick<Query, 'getShopifyMetricValues'>,
    GetShopifyMetricValuesVariables
  >(GET_SHOPIFY_METRICS_VALUES, {
    skip:
      !getMetricsKeys(debouncedMetrics, ConnectorType.SHOPIFY).length ||
      !shopifyConnectorId ||
      !dateCondition,
    onError,
    variables: {
      args: {
        metricKeys: getMetricsKeys(debouncedMetrics, ConnectorType.SHOPIFY),
        splitsKeys: getSplitsKeys(debouncedSplits, ConnectorType.SHOPIFY),
        filters: getFiltersByType(debouncedFilters, ConnectorType.SHOPIFY),
        dateRange: {
          from: adjustStartDate(debouncedStartDate),
          to: adjustEndDate(debouncedEndDate),
        },
      },
    },
  });

  // Query di comparazione per Shopify
  const { data: compareShopifyMetricValues, loading: compareShopifyLoading } =
    useQuery<
      Pick<Query, 'getShopifyMetricValues'>,
      GetShopifyMetricValuesVariables
  >(GET_SHOPIFY_METRICS_VALUES, {
    skip:
      !compareDateRange ||
      !compareDateCondition ||
      !getMetricsKeys(debouncedMetrics, ConnectorType.SHOPIFY).length ||
      !shopifyConnectorId ||
      !dateCondition,
    onError,
    variables: {
      args: {
        metricKeys: getMetricsKeys(debouncedMetrics, ConnectorType.SHOPIFY),
        splitsKeys: getSplitsKeys(debouncedSplits, ConnectorType.SHOPIFY),
        filters: getFiltersByType(debouncedFilters, ConnectorType.SHOPIFY),
        dateRange: compareDateRange
          ? {
              from: adjustStartDate(compareDateRange.from),
              to: adjustEndDate(compareDateRange.to),
            }
          : {
              from: adjustStartDate(debouncedStartDate),
              to: adjustEndDate(debouncedEndDate),
            },
      },
    },
  });

  const {
    data: magentoMetricValues,
    loading: magentoLoading,
  } = useQuery<
    Pick<Query, 'getMagentoMetricValues'>,
    GetShopifyMetricValuesVariables
  >(GET_MAGENTO_METRICS_VALUES, {
    skip:
      !getMetricsKeys(debouncedMetrics, ConnectorType.MAGENTO).length ||
      !magentoConnectorEnabled ||
      !magentoConnectorId ||
      !dateCondition,
    onError,
    variables: {
      args: {
        metricKeys: getMetricsKeys(debouncedMetrics, ConnectorType.MAGENTO),
        splitsKeys: getSplitsKeys(debouncedSplits, ConnectorType.MAGENTO),
        filters: getFiltersByType(debouncedFilters, ConnectorType.MAGENTO),
        dateRange: {
          from: adjustStartDate(debouncedStartDate),
          to: adjustEndDate(debouncedEndDate),
        },
      },
    },
  });

  const { data: compareMagentoMetricValues, loading: compareMagentoLoading } =
    useQuery<
      Pick<Query, 'getMagentoMetricValues'>,
      GetShopifyMetricValuesVariables
    >(GET_MAGENTO_METRICS_VALUES, {
      skip:
        !compareDateRange ||
        !compareDateCondition ||
        !getMetricsKeys(debouncedMetrics, ConnectorType.MAGENTO).length ||
        !magentoConnectorEnabled ||
        !magentoConnectorId ||
        !dateCondition,
      onError,
      variables: {
        args: {
          metricKeys: getMetricsKeys(debouncedMetrics, ConnectorType.MAGENTO),
          splitsKeys: getSplitsKeys(debouncedSplits, ConnectorType.MAGENTO),
          filters: getFiltersByType(debouncedFilters, ConnectorType.MAGENTO),
          dateRange: compareDateRange
            ? {
                from: adjustStartDate(compareDateRange.from),
                to: adjustEndDate(compareDateRange.to),
              }
            : {
                from: adjustStartDate(debouncedStartDate),
                to: adjustEndDate(debouncedEndDate),
              },
        },
      },
    });

  // Query di comparazione per Facebook
  const {
    data: compareFacebookMetricValues,
    loading: compareFacebookLoading,
    error: compareFacebookError,
  } = useQuery<
    Pick<Query, 'getFacebookMetricsValues'>,
    GetFacebookMetricValuesVariables
  >(GET_FACEBOOK_METRICS_VALUES, {
    skip:
      !compareDateRange ||
      !compareDateCondition ||
      !getMetricsKeys(debouncedMetrics, ConnectorType.FACEBOOK).length ||
      !facebookConnectorId ||
      !dateCondition,
    onError: (error) => {
      console.error('[FACEBOOK_COMPARE_DEBUG] Query error:', error);
      onError(error);
    },
    variables: {
      args: {
        metricKeys: getMetricsKeys(debouncedMetrics, ConnectorType.FACEBOOK),
        splitsKeys: (() => {
          const base = getSplitsKeys(debouncedSplits, ConnectorType.FACEBOOK);
          let result = [...base];

          // Mirroring per country
          if (!base.includes('target_country')) {
            const gaHasCountry = getSplitsKeys(
              debouncedSplits,
              ConnectorType.GOOGLE_ADS,
            ).includes('user_country');
            const shopifySplits = getSplitsKeys(
              debouncedSplits,
              ConnectorType.SHOPIFY,
            );
            const shopifyHasCountry =
              shopifySplits.includes('shipping_country') ||
              shopifySplits.includes('billing_country') ||
              shopifySplits.includes('country');
            const gaHasSpendMetric = getMetricsKeys(
              debouncedMetrics,
              ConnectorType.GOOGLE_ADS,
            ).some((k) => /cost|spend/i.test(k));
            const fbHasSpendMetric = getMetricsKeys(
              debouncedMetrics,
              ConnectorType.FACEBOOK,
            ).some((k) => /spend|amount/i.test(k));
            const isMarketingSpendByCountry =
              (gaHasCountry || shopifyHasCountry) &&
              (gaHasSpendMetric || fbHasSpendMetric);
            if (isMarketingSpendByCountry) {
              result.push('target_country');
            }
          }

          // Mirroring per product_type
          if (!base.includes('product_type')) {
            const gaHasProductType = getSplitsKeys(
              debouncedSplits,
              ConnectorType.GOOGLE_ADS,
            ).some(
              (k) => k === 'product_type' || k === 'segments.product_type_l1',
            );
            const shopifyHasProductType = getSplitsKeys(
              debouncedSplits,
              ConnectorType.SHOPIFY,
            ).includes('product_type');
            const hasProductTypeInOtherConnectors =
              gaHasProductType || shopifyHasProductType;
            if (hasProductTypeInOtherConnectors) {
              result.push('product_type');
            }
          }

          return result;
        })(),
        filters: getFiltersByType(debouncedFilters, ConnectorType.FACEBOOK),
        dateRange: compareDateRange
          ? {
              from: adjustStartDate(compareDateRange.from),
              to: adjustEndDate(compareDateRange.to),
            }
          : {
              from: debouncedStartDate,
              to: debouncedEndDate,
            },
      },
    },
    onCompleted: (data) => {
      console.log('[FACEBOOK_COMPARE_DEBUG] Query completed', {
        hasData: !!data?.getFacebookMetricsValues,
        dataLength: Array.isArray(data?.getFacebookMetricsValues)
          ? data.getFacebookMetricsValues.length
          : 0,
        compareDateRange,
        firstRow:
          Array.isArray(data?.getFacebookMetricsValues) &&
          data.getFacebookMetricsValues.length > 0
            ? data.getFacebookMetricsValues[0]
            : null,
      });
    },
  });

  // Query di comparazione per Google Ads
  const {
    data: compareGoogleAdsMetricValues,
    loading: compareGoogleAdsLoading,
  } = useQuery<
    Pick<Query, 'getGoogleAdsMetrics'>,
    GetGoogleAdsMetricsVariables
  >(GET_GOOGLE_ADS_METRICS_DRAFT, {
    skip:
      !compareDateRange ||
      !compareDateCondition ||
      !effectiveGoogleAdsMetricKeys.length ||
      !googleAdsConnectorId ||
      !clientAccountId ||
      !dateCondition,
    onError,
    variables: {
      args: {
        metricKeys: effectiveGoogleAdsMetricKeys,
        splitsKeys: (() => {
          const base =
            effectiveGoogleAdsSplitKeys.length > 0
              ? effectiveGoogleAdsSplitKeys
              : getSplitsKeys(debouncedSplits, ConnectorType.GOOGLE_ADS);
          if (base.includes('user_country')) return base;

          const shopifySplits = getSplitsKeys(
            debouncedSplits,
            ConnectorType.SHOPIFY,
          );
          const shopifyHasCountry =
            shopifySplits.includes('shipping_country') ||
            shopifySplits.includes('billing_country') ||
            shopifySplits.includes('country');

          const fbHasCountry = getSplitsKeys(
            debouncedSplits,
            ConnectorType.FACEBOOK,
          ).includes('target_country');

          const googleAdsHasSpendMetric = effectiveGoogleAdsMetricKeys.some(
            (k) => /cost|spend/i.test(k),
          );
          const fbHasSpendMetric = getMetricsKeys(
            debouncedMetrics,
            ConnectorType.FACEBOOK,
          ).some((k) => /spend|amount/i.test(k));

          const isMarketingSpendByCountry =
            (shopifyHasCountry || fbHasCountry) &&
            (googleAdsHasSpendMetric || fbHasSpendMetric);

          return isMarketingSpendByCountry ? [...base, 'user_country'] : base;
        })(),
        filters: getFiltersByType(debouncedFilters, ConnectorType.GOOGLE_ADS),
        dateRange: compareDateRange
          ? {
              from: adjustStartDate(compareDateRange.from),
              to: adjustEndDate(compareDateRange.to),
            }
          : {
              from: adjustStartDate(debouncedStartDate),
              to: adjustEndDate(debouncedEndDate),
            },
      },
      clientAccountId: clientAccountId!,
    },
  });

  // Query di comparazione per Google Analytics
  const {
    data: compareGoogleAnalyticsMetricValues,
    loading: compareGoogleAnalyticsLoading,
  } = useQuery<
    Pick<Query, 'getGoogleAnalytics4Metrics'>,
    GetGoogleAnalytics4MetricVariables
  >(GET_GOOGLE_ANALYTICS_4_METRICS, {
    skip:
      !compareDateRange ||
      !compareDateCondition ||
      !getMetricsKeys(debouncedMetrics, ConnectorType.GOOGlE_ANALYTICS4)
        .length ||
      !googleAnalytics4ConnectorId ||
      !dateCondition ||
      !propertyId,
    onError,
    variables: {
      propertyId,
      args: {
        dateRange: compareDateRange
          ? {
              from: adjustStartDate(compareDateRange.from),
              to: adjustEndDate(compareDateRange.to),
            }
          : {
              from: adjustStartDate(debouncedStartDate),
              to: adjustEndDate(debouncedEndDate),
            },
        metricKeys: getMetricsKeys(
          debouncedMetrics,
          ConnectorType.GOOGlE_ANALYTICS4,
        ),
        splitsKeys: getSplitsKeys(
          debouncedSplits,
          ConnectorType.GOOGlE_ANALYTICS4,
        ),
        filters: getFiltersByType(
          debouncedFilters,
          ConnectorType.GOOGlE_ANALYTICS4,
        ),
      },
    },
  });

  const memoizedAnalyticsFilters = useMemo(() => {
    return getFiltersByType(debouncedFilters, ConnectorType.GOOGlE_ANALYTICS4);
  }, [debouncedFilters]);

  const memoizedAdsFilters = useMemo(() => {
    return getFiltersByType(debouncedFilters, ConnectorType.GOOGLE_ADS);
  }, [debouncedFilters]);

  const memoizedShopifyFilters = useMemo(() => {
    return getFiltersByType(debouncedFilters, ConnectorType.SHOPIFY);
  }, [debouncedFilters]);

  const memoizedFacebookFilters = useMemo(() => {
    return getFiltersByType(debouncedFilters, ConnectorType.FACEBOOK).map(
      (filters) => filters,
    );
  }, [debouncedFilters]);

  const {
    data: googleAnalyticsMetricValues,
    loading: googleAnalticsLoading,
    refetch: refetchAnalyticsData,
  } = useQuery<
    Pick<Query, 'getGoogleAnalytics4Metrics'>,
    GetGoogleAnalytics4MetricVariables
  >(GET_GOOGLE_ANALYTICS_4_METRICS, {
    skip:
      !getMetricsKeys(debouncedMetrics, ConnectorType.GOOGlE_ANALYTICS4)
        .length ||
      !googleAnalytics4ConnectorId ||
      !dateCondition ||
      !propertyId,
    onError,
    variables: {
      propertyId,
      args: {
        dateRange: {
          from: adjustStartDate(debouncedStartDate),
          to: adjustEndDate(debouncedEndDate),
        },
        metricKeys: getMetricsKeys(
          debouncedMetrics,
          ConnectorType.GOOGlE_ANALYTICS4,
        ),
        splitsKeys: getSplitsKeys(
          debouncedSplits,
          ConnectorType.GOOGlE_ANALYTICS4,
        ),
        filters: getFiltersByType(
          debouncedFilters,
          ConnectorType.GOOGlE_ANALYTICS4,
        ),
      },
    },
  });

  const {
    data: googleSearchConsoleMetricValues,
    loading: googleSearchConsoleLoading,
  } = useQuery<
    Pick<Query, 'getGoogleSearchConsoleMetricValues'>,
    GetGoogleSearchConsoleMetricValuesVariables
  >(GET_GOOGLE_SEARCH_CONSOLE_METRIC_VALUES, {
    skip:
      !getMetricsKeys(debouncedMetrics, ConnectorType.GOOGLE_SEARCH_CONSOLE)
        .length ||
      !googleSearchConsoleConnectorId ||
      !dateCondition ||
      !searchConsoleSiteUrl,
    onError,
    variables: {
      args: {
        dateRange: {
          from: adjustStartDate(debouncedStartDate),
          to: adjustEndDate(debouncedEndDate),
        },
        metricKeys: getMetricsKeys(
          debouncedMetrics,
          ConnectorType.GOOGLE_SEARCH_CONSOLE,
        ),
        splitsKeys: getSplitsKeys(
          debouncedSplits,
          ConnectorType.GOOGLE_SEARCH_CONSOLE,
        ),
        filters: getFiltersByType(
          debouncedFilters,
          ConnectorType.GOOGLE_SEARCH_CONSOLE,
        ),
      },
    },
  });

  // Compare in background: non includere loading in areMetricValuesLoading
  // (altrimenti skeleton infinito con Previous year / periodo confronto).
  const { data: compareGoogleSearchConsoleMetricValues } = useQuery<
    Pick<Query, 'getGoogleSearchConsoleMetricValues'>,
    GetGoogleSearchConsoleMetricValuesVariables
  >(GET_GOOGLE_SEARCH_CONSOLE_METRIC_VALUES, {
    skip:
      !compareDateRange ||
      !compareDateCondition ||
      !getMetricsKeys(debouncedMetrics, ConnectorType.GOOGLE_SEARCH_CONSOLE)
        .length ||
      !googleSearchConsoleConnectorId ||
      !dateCondition ||
      !searchConsoleSiteUrl,
    onError,
    variables: {
      args: {
        dateRange: compareDateRange
          ? {
              from: adjustStartDate(compareDateRange.from),
              to: adjustEndDate(compareDateRange.to),
            }
          : {
              from: adjustStartDate(debouncedStartDate),
              to: adjustEndDate(debouncedEndDate),
            },
        metricKeys: getMetricsKeys(
          debouncedMetrics,
          ConnectorType.GOOGLE_SEARCH_CONSOLE,
        ),
        splitsKeys: getSplitsKeys(
          debouncedSplits,
          ConnectorType.GOOGLE_SEARCH_CONSOLE,
        ),
        filters: getFiltersByType(
          debouncedFilters,
          ConnectorType.GOOGLE_SEARCH_CONSOLE,
        ),
      },
    },
  });

  const {
    data: facebookMetricsValues,
    loading: facebookMetricsValuesLoading,
    refetch: refetchFacebookData,
  } = useQuery<
    Pick<Query, 'getFacebookMetricsValues'>,
    GetFacebookMetricValuesVariables
  >(GET_FACEBOOK_METRICS_VALUES, {
    skip:
      !getMetricsKeys(debouncedMetrics, ConnectorType.FACEBOOK).length ||
      !facebookConnectorId ||
      !dateCondition,
    onError,
    variables: {
      args: {
        metricKeys: getMetricsKeys(debouncedMetrics, ConnectorType.FACEBOOK),
        // Mirroring automatico dello split Country e Product Type su Facebook se selezionato su altri connettori
        splitsKeys: (() => {
          const base = getSplitsKeys(debouncedSplits, ConnectorType.FACEBOOK);
          let result = [...base];

          // Mirroring per country
          if (!base.includes('target_country')) {
            const gaHasCountry = getSplitsKeys(
              debouncedSplits,
              ConnectorType.GOOGLE_ADS,
            ).includes('user_country');
            const shopifySplits = getSplitsKeys(
              debouncedSplits,
              ConnectorType.SHOPIFY,
            );
            const shopifyHasCountry =
              shopifySplits.includes('shipping_country') ||
              shopifySplits.includes('billing_country') ||
              shopifySplits.includes('country');
            const gaHasSpendMetric = getMetricsKeys(
              debouncedMetrics,
              ConnectorType.GOOGLE_ADS,
            ).some((k) => /cost|spend/i.test(k));
            const fbHasSpendMetric = getMetricsKeys(
              debouncedMetrics,
              ConnectorType.FACEBOOK,
            ).some((k) => /spend|amount/i.test(k));
            const isMarketingSpendByCountry =
              (gaHasCountry || shopifyHasCountry) &&
              (gaHasSpendMetric || fbHasSpendMetric);
            if (isMarketingSpendByCountry) {
              result.push('target_country');
            }
          }

          // Mirroring per product_type
          if (!base.includes('product_type')) {
            const gaHasProductType = getSplitsKeys(
              debouncedSplits,
              ConnectorType.GOOGLE_ADS,
            ).some(
              (k) => k === 'product_type' || k === 'segments.product_type_l1',
            );
            const shopifyHasProductType = getSplitsKeys(
              debouncedSplits,
              ConnectorType.SHOPIFY,
            ).includes('product_type');
            const hasProductTypeInOtherConnectors =
              gaHasProductType || shopifyHasProductType;
            if (hasProductTypeInOtherConnectors) {
              result.push('product_type');
            }
          }

          return result;
        })(),
        filters: getFiltersByType(debouncedFilters, ConnectorType.FACEBOOK),
        dateRange: {
          from: debouncedStartDate,
          to: debouncedEndDate,
        },
      },
    },
  });

  // Fix: Per Kipify, includi anche gli split di Facebook e Google Ads quando ci sono split per regioni
  // Questo permette a Kipify di fare il match tra Facebook e Google Ads quando ci sono target_region e user_region
  const kipifySplitsKeys = useMemo(() => {
    const kipifySplits = getSplitsKeys(debouncedSplits, ConnectorType.KIPIFY);
    const hasTargetRegion = debouncedSplits.some(
      (s) =>
        s.key === 'target_region' && s.connectorType === ConnectorType.FACEBOOK,
    );
    const hasUserRegion = debouncedSplits.some(
      (s) =>
        s.key === 'user_region' && s.connectorType === ConnectorType.GOOGLE_ADS,
    );

    // Markup Kipify per riga: serve split product_type in query API (spesso il tipo è solo su Shopify/Facebook/GA).
    const hasKipifyMarkup = debouncedMetrics.some(
      (m) =>
        m.type === ConnectorType.KIPIFY &&
        (m.key === 'markup' || m.id === 'kipify_markup'),
    );
    const hasProductTypeOnOtherConnector = debouncedSplits.some(
      (s) =>
        (s.key === 'product_type' &&
          (s.connectorType === ConnectorType.SHOPIFY ||
            s.connectorType === ConnectorType.FACEBOOK)) ||
        (s.key === 'segments.product_type_l1' &&
          s.connectorType === ConnectorType.GOOGLE_ADS),
    );

    const regionSplits: string[] = [];
    if (hasTargetRegion) {
      regionSplits.push('target_region');
    }
    if (hasUserRegion) {
      regionSplits.push('user_region');
    }

    const productTypeForMarkup =
      hasKipifyMarkup && hasProductTypeOnOtherConnector ? ['product_type'] : [];

    return Array.from(
      new Set([...kipifySplits, ...regionSplits, ...productTypeForMarkup]),
    );
  }, [debouncedSplits, debouncedMetrics]);

  const {
    data: kipifyMetricsValues,
    loading: kipifyMetricsValuesLoading,
    refetch: refetchKipifyData,
  } = useQuery<
    Pick<Query, 'getKipifyMetricsValues'>,
    GetKipifyMetricValuesVariables
  >(GET_KIPIFY_METRICS_VALUES, {
    skip:
      !getMetricsKeys(debouncedMetrics, ConnectorType.KIPIFY).length ||
      !dateCondition,
    onError,
    variables: {
      args: {
        metricKeys: getMetricsKeys(debouncedMetrics, ConnectorType.KIPIFY),
        splitsKeys: kipifySplitsKeys,
        filters: getFiltersByType(debouncedFilters, ConnectorType.KIPIFY),
        dateRange: {
          from: adjustStartDate(debouncedStartDate),
          to: adjustEndDate(debouncedEndDate),
        },
      },
    },
  });

  // Query di comparazione per Kipify
  const {
    data: compareKipifyMetricValues,
    loading: compareKipifyLoading,
    error: compareKipifyError,
  } = useQuery<
    Pick<Query, 'getKipifyMetricsValues'>,
    GetKipifyMetricValuesVariables
  >(GET_KIPIFY_METRICS_VALUES, {
    skip:
      !compareDateRange ||
      !compareDateCondition ||
      !getMetricsKeys(debouncedMetrics, ConnectorType.KIPIFY).length ||
      !dateCondition,
    onError: (error) => {
      console.error('[KIPIFY_COMPARE_DEBUG] Query error:', error);
      onError(error);
    },
    variables: {
      args: {
        metricKeys: getMetricsKeys(debouncedMetrics, ConnectorType.KIPIFY),
        splitsKeys: kipifySplitsKeys,
        filters: getFiltersByType(debouncedFilters, ConnectorType.KIPIFY),
        dateRange: compareDateRange
          ? {
              from: adjustStartDate(compareDateRange.from),
              to: adjustEndDate(compareDateRange.to),
            }
          : {
              from: adjustStartDate(debouncedStartDate),
              to: adjustEndDate(debouncedEndDate),
            },
      },
    },
    onCompleted: (data) => {
      console.log('[KIPIFY_COMPARE_DEBUG] Query completed', {
        hasData: !!data?.getKipifyMetricsValues,
        dataLength: Array.isArray(data?.getKipifyMetricsValues)
          ? data.getKipifyMetricsValues.length
          : 0,
        compareDateRange,
        firstRow:
          Array.isArray(data?.getKipifyMetricsValues) &&
          data.getKipifyMetricsValues.length > 0
            ? data.getKipifyMetricsValues[0]
            : null,
        allMetricKeys: Array.isArray(data?.getKipifyMetricsValues)
          ? data.getKipifyMetricsValues.flatMap(
              (r: any) => r.metricValues?.map((mv: any) => mv.key) || [],
            )
          : [],
      });
    },
  });

  const { data: customFormulaValues, loading: customFormulaValuesLoading } =
    useQuery<Pick<Query, 'getCustomFormulaValues'>, GetCustomFormulaArgs>(
      GET_CUSTOM_FORMULA_VALUE,
      {
        skip:
          !getMetricsKeys(
            debouncedMetrics,
            MetricsByConnectorType.CUSTOM_FORMULA,
          ).length || !dateCondition,
        onError,
        variables: {
          args: {
            formulaIds: getMetricsKeys(
              debouncedMetrics,
              MetricsByConnectorType.CUSTOM_FORMULA,
            ),
            splitsKeys: [],
            filters: [],
            dateRange: {
              from: debouncedStartDate,
              to: debouncedEndDate,
            },
          },
        },
      },
    );

  // Query di comparazione per Custom Formulas
  const { data: compareCustomFormulaValues } = useQuery<
    Pick<Query, 'getCustomFormulaValues'>,
    GetCustomFormulaArgs
  >(GET_CUSTOM_FORMULA_VALUE, {
    skip:
      !compareDateRange ||
      !compareDateCondition ||
      !getMetricsKeys(debouncedMetrics, MetricsByConnectorType.CUSTOM_FORMULA)
        .length ||
      !dateCondition,
    onError,
    variables: {
      args: {
        formulaIds: getMetricsKeys(
          debouncedMetrics,
          MetricsByConnectorType.CUSTOM_FORMULA,
        ),
        splitsKeys: [],
        filters: [],
        dateRange: compareDateRange
          ? {
              from: compareDateRange.from,
              to: compareDateRange.to,
            }
          : {
              from: debouncedStartDate,
              to: debouncedEndDate,
            },
      },
    },
  });

  const [createCustomReport, { loading: savingReport }] = useMutation<
    Pick<Mutation, 'createCustomReport'>,
    MutationCreateCustomReportArgs
  >(CREATE_CUSTOM_REPORT);

  const handleCloseTable = () => {
    const normalizeRows = (rows: MetricValuesRow[]): MetricValuesRow[] => {
      return rows.map((row) => {
        return {
          metricValues: row.metricValues.map(({ key, value }) => ({
            key,
            value,
          })),
          splitValues: row.splitValues.map((split) => ({
            key: split?.key || 'null',
            value: split?.value || 'N/A',
          })),
        };
      });
    };

    // Il backend gestisce il timezone dello shop, quindi inviamo solo YYYY-MM-DD
    // Il backend convertirà automaticamente in UTC usando il timezone dello shop
    const startDateStr = methods.watch('startDate'); // YYYY-MM-DD
    const endDateStr = methods.watch('endDate'); // YYYY-MM-DD

    const dateRange = {
      from: startDateStr || '',
      to: endDateStr || '',
    };

    onCloseTable({
      dateRange,
      metricValuesArgsGroupedByConnector: {
        dateRange, // Il backend richiede dateRange a livello di root
        splits: getSplitsLabels(methods.watch('splits'), ConnectorType.SHOPIFY), // Splits globali
        [ConnectorType.FACEBOOK]: {
          metrics: getMetricsLabels(
            methods.watch('metrics'),
            ConnectorType.FACEBOOK,
          ),
          splits: getSplitsLabels(
            methods.watch('splits'),
            ConnectorType.FACEBOOK,
          ),
          filters: getFiltersByType(
            methods.watch('filters'),
            ConnectorType.FACEBOOK,
          ),
        },
        [ConnectorType.GOOGLE_ADS]: {
          metrics: getMetricsLabels(
            methods.watch('metrics'),
            ConnectorType.GOOGLE_ADS,
          ),
          splits: getSplitsLabels(
            methods.watch('splits'),
            ConnectorType.GOOGLE_ADS,
          ),
          filters: getFiltersByType(
            methods.watch('filters'),
            ConnectorType.GOOGLE_ADS,
          ),
        },
        [ConnectorType.GOOGlE_ANALYTICS4]: {
          metrics: getMetricsLabels(
            methods.watch('metrics'),
            ConnectorType.GOOGlE_ANALYTICS4,
          ),
          splits: getSplitsLabels(
            methods.watch('splits'),
            ConnectorType.GOOGlE_ANALYTICS4,
          ),
          filters: getFiltersByType(
            methods.watch('filters'),
            ConnectorType.GOOGlE_ANALYTICS4,
          ),
        },
        [ConnectorType.GOOGLE_SEARCH_CONSOLE]: {
          metrics: getMetricsLabels(
            methods.watch('metrics'),
            ConnectorType.GOOGLE_SEARCH_CONSOLE,
          ),
          splits: getSplitsLabels(
            methods.watch('splits'),
            ConnectorType.GOOGLE_SEARCH_CONSOLE,
          ),
          filters: getFiltersByType(
            methods.watch('filters'),
            ConnectorType.GOOGLE_SEARCH_CONSOLE,
          ),
        },
        [ConnectorType.SHOPIFY]: {
          metrics: getMetricsLabels(
            methods.watch('metrics'),
            ConnectorType.SHOPIFY,
          ),
          splits: getSplitsLabels(
            methods.watch('splits'),
            ConnectorType.SHOPIFY,
          ),
          filters: getFiltersByType(
            methods.watch('filters'),
            ConnectorType.SHOPIFY,
          ),
        },
      } as any,
      metricValuesRowsGroupedByConnector: {
        [ConnectorType.FACEBOOK]: normalizeRows(
          facebookMetricsValues?.getFacebookMetricsValues ??
            initData?.rows.facebook ??
            [],
        ),
        [ConnectorType.GOOGlE_ANALYTICS4]: normalizeRows(
          googleAnalyticsMetricValues?.getGoogleAnalytics4Metrics ??
            initData?.rows.googleAnalytics4 ??
            [],
        ),
        [ConnectorType.GOOGLE_SEARCH_CONSOLE]: normalizeRows(
          googleSearchConsoleMetricValues?.getGoogleSearchConsoleMetricValues ??
            [],
        ),
        [ConnectorType.SHOPIFY]: normalizeRows(
          shopifyMetricValues?.getShopifyMetricValues.metricRows ??
            initData?.rows.shopify ??
            [],
        ),
        [ConnectorType.GOOGLE_ADS]: normalizeRows(
          googleAds?.getGoogleAdsMetrics ?? initData?.rows.googleAds ?? [],
        ),
        [ConnectorType.KIPIFY]: normalizeRows(
          (kipifyMetricsValues?.getKipifyMetricsValues as
            | MetricValuesRow[]
            | undefined) ??
            initData?.rows?.kipify ??
            [],
        ),
        customFormula: normalizeRows(
          customFormulaValues?.getCustomFormulaValues ??
            initData?.rows.customFormula ??
            [],
        ),
      },
    });
  };

  const getTableRows = useCallback(
    ({
      googleAdsData,
      shopifyData,
      facebookData,
      googleAnalyticsData,
      googleSearchConsoleData,
      customFormulaData,
      databaseData,
      kipifyData,
      magentoData,
    }: {
      googleAdsData?: MetricValuesRow[];
      shopifyData?: MetricValuesRow[];
      facebookData?: MetricValuesRow[];
      googleAnalyticsData?: MetricValuesRow[];
      googleSearchConsoleData?: MetricValuesRow[];
      customFormulaData?: MetricValuesRow[];
      databaseData?: MetricValuesRow[];
      kipifyData?: MetricValuesRow[];
      magentoData?: MetricValuesRow[];
    }): TableRowsType[][] => {
      const dataSources: DataSourceConfig[] = [
        {
          data: googleAnalyticsData,
          type: ConnectorType.GOOGlE_ANALYTICS4,
          metrics: googleAnalyticsMetrics,
        },
        {
          data: googleSearchConsoleData,
          type: ConnectorType.GOOGLE_SEARCH_CONSOLE,
          metrics: googleSearchConsoleMetrics || [],
        },
        {
          data: googleAdsData,
          type: ConnectorType.GOOGLE_ADS,
          metrics: googleAdsMetrics,
        },
        {
          data: shopifyData,
          type: ConnectorType.SHOPIFY,
          metrics: shopifyMetrics,
        },
        {
          data: magentoData,
          type: ConnectorType.MAGENTO,
          metrics: magentoMetrics || [],
        },
        {
          data: facebookData,
          type: ConnectorType.FACEBOOK,
          metrics: facebookMetrics,
        },
        {
          data: kipifyData,
          type: ConnectorType.KIPIFY,
          metrics: [
            {
              id: 'kipify_total_marketing_spend',
              key: 'total_marketing_spend',
              label: 'Total Marketing Spend',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: ValueType.currency,
            },
            {
              id: 'kipify_markup',
              key: 'markup',
              label: 'Markup',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: ValueType.number,
            },
            {
              id: 'kipify_roas',
              key: 'roas',
              label: 'Roas',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: ValueType.number,
            },
            {
              id: 'kipify_acos',
              key: 'acos',
              label: 'ACOS',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: ValueType.percentage,
            },
            {
              id: 'kipify_acos_total',
              key: 'acos_total',
              label: 'ACOS (shipping incl.)',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: ValueType.percentage,
            },
            {
              id: 'kipify_oac',
              key: 'oac',
              label: 'OAC',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: ValueType.currency,
            },
            {
              id: 'kipify_cac',
              key: 'cac',
              label: 'CAC',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: ValueType.currency,
            },
            {
              id: 'kipify_cac_new_customers',
              key: 'cac_new_customers',
              label: 'CAC (New Customers)',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: ValueType.currency,
            },
          ],
        },
        {
          data: customFormulaData,
          type: MetricsByConnectorType.CUSTOM_FORMULA,
          metrics: transformedCustomFormulaData,
        },
        {
          data: databaseData,
          type: ConnectorType.DATABASE,
          metrics: databaseMetrics || [],
        },
      ];
      const getSplitLabel = (key: string) => {
        // Log di debug per splits selezionati
        const selectedSplits = methods.watch('splits');
        // --- PATCH: mapping search_term come keyword ---
        let split = selectedSplits.find((split) => split.key === key);
        if (!split && key === 'keyword') {
          split = selectedSplits.find(
            (split) =>
              split.key === 'ad_group_criterion.keyword.text' ||
              split.key === 'keyword',
          );
        }
        if (!split && key === 'ad_group_criterion.keyword.text') {
          split = selectedSplits.find(
            (split) =>
              split.key === 'keyword' ||
              split.key === 'ad_group_criterion.keyword.text',
          );
        }
        // PATCH: mapping search_term come keyword
        if (!split && key === 'search_term') {
          split = selectedSplits.find(
            (split) =>
              split.key === 'search_term_view.search_term' ||
              split.key === 'search_term',
          );
        }
        if (!split && key === 'search_term_view.search_term') {
          split = selectedSplits.find(
            (split) =>
              split.key === 'search_term' ||
              split.key === 'search_term_view.search_term',
          );
        }
        // Fallback: cerca tra tutti gli split attivi (anche non selezionati)
        if (!split) {
          split = splits.find((split) => split.key === key);
        }
        const label = split?.label;
        return label;
      };

      const getCurrentMetricLabel = (
        key: string,
        type: ConnectorType | MetricsByConnectorType,
      ) =>
        methods.watch('metrics').find((currentMetric) => {
          return currentMetric.key === key && currentMetric.type === type;
        })?.label;

      const createRowData = (
        key: string,
        value: string | number,
        type: ConnectorType | MetricsByConnectorType,
        isSplit: boolean,
      ): TableRowsType => {
        let displayValue = value;
        // Applica mapping per User City (solo Google Ads)
        if (
          isSplit &&
          type === ConnectorType.GOOGLE_ADS &&
          key === 'user_city'
        ) {
          displayValue = googleAdsCitiesMap[String(value)] || value;
        }
        // Applica mapping per Account Name (solo Google Ads)
        if (
          isSplit &&
          type === ConnectorType.GOOGLE_ADS &&
          (key === 'account_name' || key === 'customer.descriptive_name')
        ) {
          // Il valore dovrebbe già essere il nome dell'account dal backend
          displayValue = value || 'Unknown Account';
        }
        const label = isSplit
          ? getSplitLabel(key)
          : getCurrentMetricLabel(key, type);

        // Usa l'icona di Kipify per kipify_country
        const iconType =
          isSplit && key === 'kipify_country' ? ConnectorType.KIPIFY : type;

        // Cambia anche il type per kipify_country
        const finalType =
          isSplit && key === 'kipify_country' ? ConnectorType.KIPIFY : type;

        // Per le metriche Kipify, imposta sempre l'icona anche se il label non viene trovato
        const shouldShowIcon =
          label ||
          (!isSplit &&
            (type === ConnectorType.KIPIFY ||
              type === MetricsByConnectorType.KIPIFY));

        return {
          id: key,
          columnId: buildColumnId(key, finalType),
          title: label || getTitle(key),
          type: finalType,
          icon: shouldShowIcon ? getIconForMetric(iconType) : undefined,
          value: displayValue,
          isSplit,
        };
      };

      // Funzione per normalizzare i codici paese
      const uniqTableRow: TableRowsType[] = [];

      // Popola uniqTableRow con tutti gli splits e metrics selezionati PRIMA di processare i dati
      const selectedSplits = methods.watch('splits') || [];
      const selectedMetrics = methods.watch('metrics') || [];
      const isSelectedColumn = (
        key: string,
        type: ConnectorType | MetricsByConnectorType,
        isSplit: boolean,
      ) => {
        const requestedColumnId = buildColumnId(key, type);
        if (isSplit) {
          return selectedSplits.some((split) => {
            if (split.connectorType !== type) return false;
            if (type === ConnectorType.GOOGLE_ADS) {
              return googleAdsSplitCatalogMatchesRowKey(split.key, key);
            }
            return cellsMatchColumnId(
              buildColumnId(split.key, split.connectorType),
              requestedColumnId,
            );
          });
        }

        return selectedMetrics.some((metric) => {
          if (metric.type !== type) return false;
          if (type === ConnectorType.GOOGLE_ADS) {
            return googleAdsMetricCatalogMatchesRowKey(metric.key, key);
          }
          return cellsMatchColumnId(
            buildColumnId(metric.key, metric.type),
            requestedColumnId,
          );
        });
      };

      // Aggiungi tutti gli splits selezionati
      selectedSplits.forEach((split) => {
        const newData = createRowData(
          split.key,
          '-',
          split.connectorType,
          true,
        );
        if (!uniqTableRow.some((item) => item.columnId === newData.columnId)) {
          uniqTableRow.push(newData);
        }
      });

      // Aggiungi tutte le metrics selezionate
      selectedMetrics.forEach((metric) => {
        const newData = createRowData(metric.key, '-', metric.type, false);
        if (!uniqTableRow.some((item) => item.columnId === newData.columnId)) {
          uniqTableRow.push(newData);
        }
      });

      const processDataSource = (
        dataSource: DataSourceConfig,
      ): TableRowsType[][] => {
        if (!dataSource.data?.length) return [];

        // Logica originale per processare tutti i dati senza raggruppamento
        return dataSource.data.map(({ metricValues, splitValues }) => {
          const processValues = (
            values: (MetricValue | null)[],
            isSplit: boolean,
          ) =>
            values.map((cell) => {
              const newData = createRowData(
                cell?.key ?? 'null',
                (cell?.value === 'undefined' ? undefined : cell?.value) ??
                  notApplicableValue,
                dataSource.type,
                isSplit,
              );

              // Mantieni visibili solo colonne selezionate dall'utente.
              // Le metriche helper non selezionate restano nella riga ma non entrano nell'header.
              if (isSelectedColumn(newData.id, newData.type, isSplit)) {
                const existingIndex = uniqTableRow.findIndex((val) =>
                  cellsMatchColumnId(val.columnId, newData.columnId),
                );
                if (existingIndex !== -1) {
                  // Conserva columnId/title del catalogo (es. segments.ad_network_type):
                  // l'API usa chiavi logiche (ad_network) e se sovrascriviamo l'id,
                  // drag&drop / columnOrder non trovano più la colonna (resta "fissa" a sinistra).
                  const catalogCell = uniqTableRow[existingIndex];
                  uniqTableRow[existingIndex] = {
                    ...newData,
                    columnId: catalogCell.columnId,
                    id: catalogCell.id,
                    title: catalogCell.title || newData.title,
                  };
                } else {
                  uniqTableRow.push(newData);
                }
              }

              return newData;
            });

          const splitsData = processValues(splitValues, true);
          const metricData = processValues(metricValues, false);

          return [...splitsData, ...metricData];
        });
      };

      let rows: TableRowsType[][] = [];

      // Process all data sources
      dataSources.forEach((dataSource) => {
        const processedRows = processDataSource(dataSource);
        rows = [...rows, ...processedRows];
      });

      const hasKipifyCountry = selectedSplits.some(
        (split) => split.key === 'kipify_country',
      );

      // Raggruppiamo lato frontend in modo deterministico, mantenendo billing country a disposizione.
      rows = groupRowsByCountry(rows, { hasKipifyCountry });

      // Allineamento per Product Title tra connettori
      const productTitleKeyMap: Partial<Record<ConnectorType, string[]>> = {
        [ConnectorType.FACEBOOK]: ['product_title'],
        [ConnectorType.GOOGLE_ADS]: ['segments.product_title', 'product_title'],
        [ConnectorType.SHOPIFY]: ['product_title', 'li.title'],
      };

      // Allineamento per Product Type tra connettori
      const productTypeKeyMap: Partial<Record<ConnectorType, string[]>> = {
        [ConnectorType.FACEBOOK]: ['product_type'],
        [ConnectorType.GOOGLE_ADS]: [
          'segments.product_type_l1',
          'product_type',
        ],
        [ConnectorType.SHOPIFY]: ['product_type'],
      };

      // Allineamento Vendor (Shopify) ↔ Product Brand (Google Ads)
      // Obiettivo: permettere di vedere "Spend" Google Ads accanto ai vendor Shopify
      // quando l'utente seleziona Vendor + Product Brand come split.
      const vendorBrandKeyMap: Partial<Record<ConnectorType, string[]>> = {
        [ConnectorType.SHOPIFY]: ['vendor'],
        [ConnectorType.GOOGLE_ADS]: ['segments.product_brand', 'product_brand'],
      };

      // Normalizzazione forte per il join dei product title (rimuove accenti, simboli,
      // taglia parti da pipe e size/sKU comuni, compatta spazi)
      const normalizeProductTitleValue = (raw: string) => {
        if (!raw) return '';
        let value = String(raw);
        // Taglia dopo il pipe o doppio spazio separatore comune per varianti
        value = value.split('|')[0];
        // Normalizza unicode e rimuove accenti
        value = value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
        // Rimuove pattern size/sku più comuni
        value = value
          // es. "US/UK 32W - IT 46", "US/UK 38W", "IT 48"
          .replace(/\bUS\/UK\s*\d+\w?\b/gi, ' ')
          .replace(/\bIT\s*\d+\b/gi, ' ')
          // es. taglie note
          .replace(/\b(XXS|XS|S|M|L|XL|XXL|XXXL)\b/gi, ' ')
          // rimuove numerazioni isolate alla fine tipo " 44", " 46", " 48"
          .replace(/\b\d{2,3}\b/g, ' ')
          // rimuove trattini e descrittori comuni di varianti
          .replace(/[-_]+/g, ' ');
        // Solo alfanumerico e spazi, lowercase e trimming
        value = value
          .replace(/[^a-z0-9]+/gi, ' ')
          .trim()
          .toLowerCase();
        // Compatta spazi multipli
        value = value.replace(/\s{2,}/g, ' ');
        return value;
      };

      const selectedProductTitleKeys = new Map<ConnectorType, string[]>();

      selectedSplits.forEach((split) => {
        const connectorType = split.connectorType as ConnectorType;
        const allowedKeys = productTitleKeyMap[connectorType];
        if (allowedKeys && allowedKeys.indexOf(split.key) !== -1) {
          selectedProductTitleKeys.set(connectorType, allowedKeys);
        }
      });

      if (selectedProductTitleKeys.size >= 2) {
        const joinRowIndex = new Map<string, number>();
        const joinCellMaps = new Map<string, Map<string, TableRowsType>>();
        // Mantiene titolo canonico per ogni chiave normalizzata (priorità: Shopify > Facebook > Google)
        const canonicalTitleByKey = new Map<
          string,
          { title: string; source: ConnectorType }
        >();
        const priority: ConnectorType[] = [
          ConnectorType.SHOPIFY,
          ConnectorType.FACEBOOK,
          ConnectorType.GOOGLE_ADS,
        ];
        const getPriority = (src?: ConnectorType) => {
          const idx = src ? priority.indexOf(src) : -1;
          return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
        };
        const alignedRows: TableRowsType[][] = [];

        rows.forEach((row) => {
          if (!row || row.length === 0) {
            alignedRows.push(row);
            return;
          }

          const firstSplit = row.find((cell) => cell.isSplit);
          const rowConnectorType = firstSplit?.type as
            | ConnectorType
            | undefined;

          if (!rowConnectorType) {
            alignedRows.push(row);
            return;
          }

          const allowedKeys = selectedProductTitleKeys.get(rowConnectorType);

          if (!allowedKeys || !allowedKeys.length) {
            alignedRows.push(row);
            return;
          }

          const joinCell = row.find(
            (cell) =>
              cell.isSplit &&
              cell.type === rowConnectorType &&
              allowedKeys.indexOf(cell.id) !== -1,
          );

          const joinValue =
            joinCell && joinCell.value !== undefined && joinCell.value !== null
              ? String(joinCell.value)
              : '';

          const normalizedKey = normalizeProductTitleValue(joinValue);

          if (!normalizedKey) {
            alignedRows.push(row);
            return;
          }

          // Aggiorna titolo canonico per questo gruppo se necessario
          if (joinValue) {
            const current = canonicalTitleByKey.get(normalizedKey);
            if (
              !current ||
              getPriority(rowConnectorType) < getPriority(current.source)
            ) {
              canonicalTitleByKey.set(normalizedKey, {
                title: joinValue,
                source: rowConnectorType!,
              });
            }
          }

          let targetIndex = joinRowIndex.get(normalizedKey);
          let cellMap =
            targetIndex !== undefined
              ? joinCellMaps.get(normalizedKey)
              : undefined;

          if (targetIndex === undefined || !cellMap) {
            targetIndex = alignedRows.length;
            joinRowIndex.set(normalizedKey, targetIndex);
            cellMap = new Map<string, TableRowsType>();
            joinCellMaps.set(normalizedKey, cellMap);
            alignedRows.push([]);
          }

          row.forEach((cell) => {
            cellMap!.set(cell.columnId, cell);
          });

          alignedRows[targetIndex!] = Array.from(cellMap.values());
        });

        // Applica valore canonico del titolo prodotto su tutte le celle di quel gruppo
        rows = alignedRows.map((row) => {
          if (!row || row.length === 0) return row;
          // Trova un product_title valido nella riga per calcolare la chiave normalizzata
          const anyJoinCell = row.find(
            (cell) =>
              cell.isSplit &&
              Array.isArray(productTitleKeyMap[cell.type as ConnectorType]) &&
              (
                productTitleKeyMap[cell.type as ConnectorType] as string[]
              ).includes(cell.id),
          );
          const rawValue =
            anyJoinCell && anyJoinCell.value != null
              ? String(anyJoinCell.value)
              : '';
          const normalizedKey = normalizeProductTitleValue(rawValue);
          const canonical = normalizedKey
            ? canonicalTitleByKey.get(normalizedKey)?.title
            : undefined;
          if (!canonical) return row;
          // Sovrascrive i product_title per connettori noti con il titolo canonico
          return row.map((cell) => {
            if (
              cell.isSplit &&
              Array.isArray(productTitleKeyMap[cell.type as ConnectorType]) &&
              (
                productTitleKeyMap[cell.type as ConnectorType] as string[]
              ).includes(cell.id)
            ) {
              return { ...cell, value: canonical };
            }
            return cell;
          });
        });
      }

      // Allineamento Vendor/Brand tra connettori (Shopify e Google Ads)
      const selectedVendorBrandKeys = new Map<ConnectorType, string[]>();

      selectedSplits.forEach((split) => {
        const connectorType = split.connectorType as ConnectorType;
        const allowedKeys = vendorBrandKeyMap[connectorType];
        if (allowedKeys && allowedKeys.indexOf(split.key) !== -1) {
          selectedVendorBrandKeys.set(connectorType, allowedKeys);
        }
      });

      if (selectedVendorBrandKeys.size >= 2) {
        // Quando uniamo Shopify Vendor ↔ Google Ads Brand, può capitare che Shopify abbia
        // più righe che normalizzano allo stesso valore (es. "Nohow" vs "NOHOW").
        // In quel caso NON vogliamo perdere metriche: sommiamo i valori numerici anziché sovrascrivere.
        const parseNumericValueForJoin = (raw: unknown): number | null => {
          if (raw === null || raw === undefined) return null;
          const s = String(raw).trim();
          if (!s) return null;

          // Tieni solo cifre e separatori base; rimuovi simboli (€, %, spazi, ecc.)
          let cleaned = s.replace(/[^\d,.-]/g, '');
          if (!cleaned) return null;

          const hasComma = cleaned.includes(',');
          const hasDot = cleaned.includes('.');
          // Assunzione conservativa: se compaiono sia "." che ",", trattiamo "." come separatore migliaia e "," come decimale (formato IT).
          if (hasComma && hasDot) {
            cleaned = cleaned.replace(/\./g, '').replace(/,/g, '.');
          } else if (hasComma && !hasDot) {
            cleaned = cleaned.replace(/,/g, '.');
          }

          const n = Number(cleaned);
          return Number.isFinite(n) ? n : null;
        };

        const joinRowIndex = new Map<string, number>();
        const joinCellMaps = new Map<string, Map<string, TableRowsType>>();
        // Mantiene valore canonico per ogni chiave (priorità: Shopify > Google Ads)
        const canonicalByKey = new Map<
          string,
          { value: string; source: ConnectorType }
        >();
        const priority: ConnectorType[] = [
          ConnectorType.SHOPIFY,
          ConnectorType.GOOGLE_ADS,
        ];
        const getPriority = (src?: ConnectorType) => {
          const idx = src ? priority.indexOf(src) : -1;
          return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
        };
        const alignedRows: TableRowsType[][] = [];

        rows.forEach((row) => {
          if (!row || row.length === 0) {
            alignedRows.push(row);
            return;
          }

          const firstSplit = row.find((cell) => cell.isSplit);
          const rowConnectorType = firstSplit?.type as
            | ConnectorType
            | undefined;

          if (!rowConnectorType) {
            alignedRows.push(row);
            return;
          }

          const allowedKeys = selectedVendorBrandKeys.get(rowConnectorType);
          if (!allowedKeys || !allowedKeys.length) {
            alignedRows.push(row);
            return;
          }

          const joinCell = row.find(
            (cell) =>
              cell.isSplit &&
              cell.type === rowConnectorType &&
              allowedKeys.indexOf(cell.id) !== -1,
          );

          const joinValue =
            joinCell && joinCell.value !== undefined && joinCell.value !== null
              ? String(joinCell.value)
              : '';

          const normalizedKey = normalizeVendorBrandValue(joinValue);
          if (!normalizedKey) {
            alignedRows.push(row);
            return;
          }

          if (joinValue) {
            const current = canonicalByKey.get(normalizedKey);
            if (
              !current ||
              getPriority(rowConnectorType) < getPriority(current.source)
            ) {
              canonicalByKey.set(normalizedKey, {
                value: joinValue,
                source: rowConnectorType,
              });
            }
          }

          let targetIndex = joinRowIndex.get(normalizedKey);
          let cellMap =
            targetIndex !== undefined
              ? joinCellMaps.get(normalizedKey)
              : undefined;

          if (targetIndex === undefined || !cellMap) {
            targetIndex = alignedRows.length;
            joinRowIndex.set(normalizedKey, targetIndex);
            cellMap = new Map<string, TableRowsType>();
            joinCellMaps.set(normalizedKey, cellMap);
            alignedRows.push([]);
          }

          row.forEach((cell) => {
            const existing = cellMap!.get(cell.columnId);

            // Se esiste già una metrica identica (stesso connector + id) sommiamo i valori numerici
            // per evitare che una riga "sovrascriva" l'altra.
            if (
              existing &&
              !existing.isSplit &&
              !cell.isSplit &&
              existing.type === cell.type &&
              existing.id === cell.id
            ) {
              const a = parseNumericValueForJoin(existing.value);
              const b = parseNumericValueForJoin(cell.value);
              if (a !== null && b !== null) {
                cellMap!.set(cell.columnId, {
                  ...existing,
                  value: String(a + b),
                });
                return;
              }
              // fallback: se uno dei due non è numerico, manteniamo quello esistente (più stabile)
              return;
            }

            cellMap!.set(cell.columnId, cell);
          });
          alignedRows[targetIndex] = Array.from(cellMap.values());
        });

        // Applica valore canonico vendor/brand su tutte le celle del gruppo
        rows = alignedRows.map((row) => {
          if (!row || row.length === 0) return row;
          const anyJoinCell = row.find(
            (cell) =>
              cell.isSplit &&
              Array.isArray(vendorBrandKeyMap[cell.type as ConnectorType]) &&
              (
                vendorBrandKeyMap[cell.type as ConnectorType] as string[]
              ).includes(cell.id),
          );
          const rawValue =
            anyJoinCell && anyJoinCell.value != null
              ? String(anyJoinCell.value)
              : '';
          const normalizedKey = normalizeVendorBrandValue(rawValue);
          const canonical = normalizedKey
            ? canonicalByKey.get(normalizedKey)?.value
            : undefined;
          if (!canonical) return row;
          return row.map((cell) => {
            if (
              cell.isSplit &&
              Array.isArray(vendorBrandKeyMap[cell.type as ConnectorType]) &&
              (
                vendorBrandKeyMap[cell.type as ConnectorType] as string[]
              ).includes(cell.id)
            ) {
              return { ...cell, value: canonical };
            }
            return cell;
          });
        });
      }

      // Allineamento per Product Type tra connettori (Shopify e Google Ads)
      const selectedProductTypeKeys = new Map<ConnectorType, string[]>();

      selectedSplits.forEach((split) => {
        const connectorType = split.connectorType as ConnectorType;
        const allowedKeys = productTypeKeyMap[connectorType];
        if (allowedKeys && allowedKeys.indexOf(split.key) !== -1) {
          selectedProductTypeKeys.set(connectorType, allowedKeys);
        }
      });

      if (selectedProductTypeKeys.size >= 2) {
        // Normalizzazione semplice per product_type (case-insensitive, trim)
        const normalizeProductTypeValue = (raw: string) => {
          if (!raw) return '';
          return String(raw)
            .trim()
            .toLowerCase()
            .replace(/\s{2,}/g, ' ');
        };

        const joinRowIndex = new Map<string, number>();
        const joinCellMaps = new Map<string, Map<string, TableRowsType>>();
        // Mantiene tipo canonico per ogni chiave normalizzata (priorità: Shopify > Google Ads)
        const canonicalTypeByKey = new Map<
          string,
          { type: string; source: ConnectorType }
        >();
        const priority: ConnectorType[] = [
          ConnectorType.SHOPIFY,
          ConnectorType.FACEBOOK,
          ConnectorType.GOOGLE_ADS,
        ];
        const getPriority = (src?: ConnectorType) => {
          const idx = src ? priority.indexOf(src) : -1;
          return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
        };
        const alignedRows: TableRowsType[][] = [];

        rows.forEach((row) => {
          if (!row || row.length === 0) {
            alignedRows.push(row);
            return;
          }

          const firstSplit = row.find((cell) => cell.isSplit);
          const rowConnectorType = firstSplit?.type as
            | ConnectorType
            | undefined;

          if (!rowConnectorType) {
            alignedRows.push(row);
            return;
          }

          const allowedKeys = selectedProductTypeKeys.get(rowConnectorType);

          if (!allowedKeys || !allowedKeys.length) {
            alignedRows.push(row);
            return;
          }

          const joinCell = row.find(
            (cell) =>
              cell.isSplit &&
              cell.type === rowConnectorType &&
              allowedKeys.indexOf(cell.id) !== -1,
          );

          const joinValue =
            joinCell && joinCell.value !== undefined && joinCell.value !== null
              ? String(joinCell.value)
              : '';

          const normalizedKey = normalizeProductTypeValue(joinValue);

          if (!normalizedKey) {
            alignedRows.push(row);
            return;
          }

          // Aggiorna tipo canonico per questo gruppo se necessario
          if (joinValue) {
            const current = canonicalTypeByKey.get(normalizedKey);
            if (
              !current ||
              getPriority(rowConnectorType) < getPriority(current.source)
            ) {
              canonicalTypeByKey.set(normalizedKey, {
                type: joinValue,
                source: rowConnectorType!,
              });
            }
          }

          let targetIndex = joinRowIndex.get(normalizedKey);
          let cellMap =
            targetIndex !== undefined
              ? joinCellMaps.get(normalizedKey)
              : undefined;

          if (targetIndex === undefined || !cellMap) {
            targetIndex = alignedRows.length;
            joinRowIndex.set(normalizedKey, targetIndex);
            cellMap = new Map<string, TableRowsType>();
            joinCellMaps.set(normalizedKey, cellMap);
            alignedRows.push([]);
          }

          row.forEach((cell) => {
            cellMap!.set(cell.columnId, cell);
          });

          alignedRows[targetIndex!] = Array.from(cellMap.values());
        });

        // Applica valore canonico del tipo prodotto su tutte le celle di quel gruppo
        rows = alignedRows.map((row) => {
          if (!row || row.length === 0) return row;
          // Trova un product_type valido nella riga per calcolare la chiave normalizzata
          const anyJoinCell = row.find(
            (cell) =>
              cell.isSplit &&
              Array.isArray(productTypeKeyMap[cell.type as ConnectorType]) &&
              (
                productTypeKeyMap[cell.type as ConnectorType] as string[]
              ).includes(cell.id),
          );
          const rawValue =
            anyJoinCell && anyJoinCell.value != null
              ? String(anyJoinCell.value)
              : '';
          const normalizedKey = normalizeProductTypeValue(rawValue);
          const canonical = normalizedKey
            ? canonicalTypeByKey.get(normalizedKey)?.type
            : undefined;
          if (!canonical) return row;
          // Sovrascrive i product_type per connettori noti con il tipo canonico
          return row.map((cell) => {
            if (
              cell.isSplit &&
              Array.isArray(productTypeKeyMap[cell.type as ConnectorType]) &&
              (
                productTypeKeyMap[cell.type as ConnectorType] as string[]
              ).includes(cell.id)
            ) {
              return { ...cell, value: canonical };
            }
            return cell;
          });
        });
      }

      // Join per metriche di marketing spend (Google Ads + Facebook + Kipify Total Marketing Spend)
      // Rileva se ci sono metriche di spend selezionate
      // Fix: migliorato riconoscimento metriche spend per Google Ads (metrics.cost, metrics.cost_micros, advertising_spend, cost)
      const hasGoogleAdsSpend = selectedMetrics.some(
        (m) =>
          m.type === ConnectorType.GOOGLE_ADS &&
          (/advertising_spend|metrics\.cost|cost_micros|^cost$|spend/i.test(
            m.key,
          ) ||
            /spend|cost/i.test(m.label || '')),
      );
      // Fix: migliorato riconoscimento metriche spend per Facebook (spend, amount_spent)
      const hasFacebookSpend = selectedMetrics.some(
        (m) =>
          m.type === ConnectorType.FACEBOOK &&
          (/spend|amount_spent|amount/i.test(m.key) ||
            /spend|amount/i.test(m.label || '')),
      );
      const hasKipifyTotalMarketingSpend = selectedMetrics.some(
        (m) =>
          (m.type === ConnectorType.KIPIFY ||
            m.type === MetricsByConnectorType.KIPIFY) &&
          (/total_marketing_spend/i.test(m.key) ||
            /total.*marketing.*spend/i.test(m.label || '')),
      );

      // Fix: Rileva se ci sono metriche Shopify di revenue/sales/quantity (gross_sales, total_sales, net_sales, product_quantity, ecc.)
      const hasShopifyRevenue = selectedMetrics.some(
        (m) =>
          m.type === ConnectorType.SHOPIFY &&
          (/gross.*sales|total.*sales|net.*sales|revenue|sales|product.*quantity|quantity.*sold|items.*sold|units.*sold|net.*product.*quantity|quantity/i.test(
            m.key,
          ) ||
            /gross.*sales|total.*sales|net.*sales|revenue|sales|product.*quantity|quantity.*sold|items.*sold|units.*sold|net.*product.*quantity|quantity/i.test(
              m.label || '',
            )),
      );

      // Attiva join SOLO se ci sono almeno 2 metriche spend da connettori diversi
      // (Google Ads Spend, Facebook Spend, Kipify Total Marketing Spend)
      // NON attivare il join se c'è solo una metrica spend + Shopify revenue
      // perché questo causerebbe l'unione di tutte le righe in una sola
      const spendMetricsCount = [
        hasGoogleAdsSpend,
        hasFacebookSpend,
        hasKipifyTotalMarketingSpend,
      ].filter(Boolean).length;

      const shouldJoinMarketingSpend =
        spendMetricsCount >= 2 ||
        // Include Shopify solo se ci sono almeno 2 metriche spend (non solo 1)
        (hasShopifyRevenue && spendMetricsCount >= 2);

      if (shouldJoinMarketingSpend) {
        // Crea una mappa per raggruppare righe per chiave di join
        // Se ci sono splits comuni, usa quelli come chiave di join
        // Altrimenti, crea una singola riga totale
        const joinKeyMap = new Map<string, Map<string, TableRowsType>>();
        const otherRows: TableRowsType[][] = []; // Righe di altri connettori

        // Trova splits comuni tra Google Ads, Facebook, Kipify e Shopify (se incluso)
        const commonSplits = selectedSplits.filter((split) => {
          const isGoogleAds = split.connectorType === ConnectorType.GOOGLE_ADS;
          const isFacebook = split.connectorType === ConnectorType.FACEBOOK;
          const isKipify = split.connectorType === ConnectorType.KIPIFY;
          const isShopify = split.connectorType === ConnectorType.SHOPIFY;

          // Se lo split è kipify_country, è sempre comune
          if (split.key === 'kipify_country') return true;

          // Se lo split è presente in almeno 2 connettori tra Google Ads, Facebook, Kipify e Shopify, è comune
          const hasInGoogleAds =
            isGoogleAds ||
            selectedSplits.some(
              (s) =>
                s.connectorType === ConnectorType.GOOGLE_ADS &&
                splitKeysEquivalent(s.key, split.key),
            );
          const hasInFacebook =
            isFacebook ||
            selectedSplits.some(
              (s) =>
                s.connectorType === ConnectorType.FACEBOOK &&
                splitKeysEquivalent(s.key, split.key),
            );
          const hasInKipify =
            isKipify ||
            selectedSplits.some(
              (s) =>
                s.connectorType === ConnectorType.KIPIFY &&
                splitKeysEquivalent(s.key, split.key),
            );
          const hasInShopify =
            isShopify ||
            selectedSplits.some(
              (s) =>
                s.connectorType === ConnectorType.SHOPIFY &&
                splitKeysEquivalent(s.key, split.key),
            );

          const count = [
            hasInGoogleAds,
            hasInFacebook,
            hasInKipify,
            hasInShopify,
          ].filter(Boolean).length;
          return count >= 2;
        });

        // Fix: Se ci sono target_region (Facebook) e user_region (Google Ads),
        // aggiungili come split comune normalizzato a kipify_country per il join
        const hasTargetRegion = selectedSplits.some(
          (s) =>
            s.key === 'target_region' &&
            s.connectorType === ConnectorType.FACEBOOK,
        );
        const hasUserRegion = selectedSplits.some(
          (s) =>
            s.key === 'user_region' &&
            s.connectorType === ConnectorType.GOOGLE_ADS,
        );

        if (hasTargetRegion && hasUserRegion) {
          // Aggiungi kipify_country come split comune se non è già presente
          const hasKipifyCountryInCommon = commonSplits.some(
            (s) => s.key === 'kipify_country',
          );
          if (!hasKipifyCountryInCommon) {
            // Usa target_region come base per creare uno split comune normalizzato
            const targetRegionSplit = selectedSplits.find(
              (s) =>
                s.key === 'target_region' &&
                s.connectorType === ConnectorType.FACEBOOK,
            );
            if (targetRegionSplit) {
              commonSplits.push({
                ...targetRegionSplit,
                key: 'kipify_country', // Normalizza a kipify_country per il join
              });
            }
          }
        }

        // Fix: Se c'è un join per product_title attivo, aggiungi product_title come split comune
        // per evitare che tutte le righe vengano unite in una sola
        if (selectedProductTitleKeys.size >= 2) {
          // Verifica se product_title è già presente nei commonSplits
          const hasProductTitleInCommon = commonSplits.some(
            (s) =>
              s.key === 'product_title' ||
              s.key === 'segments.product_title' ||
              s.key === 'li.title',
          );

          if (!hasProductTitleInCommon) {
            // Aggiungi product_title come split comune se presente in almeno 2 connettori
            const hasProductTitleInGoogleAds = selectedSplits.some(
              (s) =>
                (s.key === 'product_title' ||
                  s.key === 'segments.product_title') &&
                s.connectorType === ConnectorType.GOOGLE_ADS,
            );
            const hasProductTitleInFacebook = selectedSplits.some(
              (s) =>
                s.key === 'product_title' &&
                s.connectorType === ConnectorType.FACEBOOK,
            );
            const hasProductTitleInShopify = selectedSplits.some(
              (s) =>
                (s.key === 'product_title' || s.key === 'li.title') &&
                s.connectorType === ConnectorType.SHOPIFY,
            );

            // Conta quanti connettori hanno product_title
            const productTitleCount = [
              hasProductTitleInGoogleAds,
              hasProductTitleInFacebook,
              hasProductTitleInShopify,
            ].filter(Boolean).length;

            if (productTitleCount >= 2) {
              // Aggiungi product_title come split comune (usa la versione di Facebook o Shopify come riferimento)
              const facebookProductTitle = selectedSplits.find(
                (s) =>
                  s.key === 'product_title' &&
                  s.connectorType === ConnectorType.FACEBOOK,
              );
              const shopifyProductTitle = selectedSplits.find(
                (s) =>
                  (s.key === 'product_title' || s.key === 'li.title') &&
                  s.connectorType === ConnectorType.SHOPIFY,
              );
              const googleAdsProductTitle = selectedSplits.find(
                (s) =>
                  (s.key === 'product_title' ||
                    s.key === 'segments.product_title') &&
                  s.connectorType === ConnectorType.GOOGLE_ADS,
              );

              // Priorità: Shopify > Facebook > Google Ads (per coerenza con join product_title)
              if (shopifyProductTitle) {
                commonSplits.push({
                  ...shopifyProductTitle,
                  key: 'product_title', // Normalizza a product_title per il join
                });
              } else if (facebookProductTitle) {
                commonSplits.push({
                  ...facebookProductTitle,
                  key: 'product_title', // Normalizza a product_title per il join
                });
              } else if (googleAdsProductTitle) {
                commonSplits.push({
                  ...googleAdsProductTitle,
                  key: 'product_title', // Normalizza a product_title per il join
                });
              }
            }
          }
        }

        // Fix: Se c'è un join per product_type attivo, aggiungi product_type come split comune
        // per evitare che tutte le righe vengano unite in una sola
        if (selectedProductTypeKeys.size >= 2) {
          // Verifica se product_type è già presente nei commonSplits
          const hasProductTypeInCommon = commonSplits.some(
            (s) =>
              s.key === 'product_type' || s.key === 'segments.product_type_l1',
          );

          if (!hasProductTypeInCommon) {
            // Aggiungi product_type come split comune se presente in almeno 2 connettori
            const hasProductTypeInGoogleAds = selectedSplits.some(
              (s) =>
                (s.key === 'product_type' ||
                  s.key === 'segments.product_type_l1') &&
                s.connectorType === ConnectorType.GOOGLE_ADS,
            );
            const hasProductTypeInFacebook = selectedSplits.some(
              (s) =>
                s.key === 'product_type' &&
                s.connectorType === ConnectorType.FACEBOOK,
            );
            const hasProductTypeInShopify = selectedSplits.some(
              (s) =>
                s.key === 'product_type' &&
                s.connectorType === ConnectorType.SHOPIFY,
            );

            // Conta quanti connettori hanno product_type
            const productTypeCount = [
              hasProductTypeInGoogleAds,
              hasProductTypeInFacebook,
              hasProductTypeInShopify,
            ].filter(Boolean).length;

            if (productTypeCount >= 2) {
              // Aggiungi product_type come split comune (usa la versione di Shopify come riferimento)
              const facebookProductType = selectedSplits.find(
                (s) =>
                  s.key === 'product_type' &&
                  s.connectorType === ConnectorType.FACEBOOK,
              );
              const shopifyProductType = selectedSplits.find(
                (s) =>
                  s.key === 'product_type' &&
                  s.connectorType === ConnectorType.SHOPIFY,
              );
              const googleAdsProductType = selectedSplits.find(
                (s) =>
                  (s.key === 'product_type' ||
                    s.key === 'segments.product_type_l1') &&
                  s.connectorType === ConnectorType.GOOGLE_ADS,
              );

              // Priorità: Shopify > Facebook > Google Ads
              if (shopifyProductType) {
                commonSplits.push({
                  ...shopifyProductType,
                  key: 'product_type', // Normalizza a product_type per il join
                });
              } else if (facebookProductType) {
                commonSplits.push({
                  ...facebookProductType,
                  key: 'product_type', // Normalizza a product_type per il join
                });
              } else if (googleAdsProductType) {
                commonSplits.push({
                  ...googleAdsProductType,
                  key: 'product_type', // Normalizza a product_type per il join
                });
              }
            }
          }
        }

        // Search term / keyword spesso solo su Google Ads: senza commonSplits il join marketing
        // (2+ spend) collassava tutto su una chiave troppo grossolana (es. solo paese) e ROAS
        // risultava una sola cella per molte righe logiche.
        if (spendMetricsCount >= 2) {
          const hasSearchTermInCommon = commonSplits.some(
            (s) => joinSplitNormalizedKey(s.key) === 'search_term',
          );
          const googleSearchTermSplit = selectedSplits.find(
            (s) =>
              s.connectorType === ConnectorType.GOOGLE_ADS &&
              joinSplitNormalizedKey(s.key) === 'search_term',
          );
          if (!hasSearchTermInCommon && googleSearchTermSplit) {
            commonSplits.push({
              ...googleSearchTermSplit,
              key: 'search_term',
            });
          }

          const hasKeywordInCommon = commonSplits.some(
            (s) => joinSplitNormalizedKey(s.key) === 'keyword',
          );
          const googleKeywordSplit = selectedSplits.find(
            (s) =>
              s.connectorType === ConnectorType.GOOGLE_ADS &&
              joinSplitNormalizedKey(s.key) === 'keyword',
          );
          if (!hasKeywordInCommon && googleKeywordSplit) {
            commonSplits.push({
              ...googleKeywordSplit,
              key: 'keyword',
            });
          }
        }

        // Se non ci sono split comuni, NON unire le righe con joinKey='total':
        // causerebbe la perdita del dettaglio (tutto in una riga).
        // In questi casi lasciamo le righe separate e lasciamo che `groupRowsByCountry`
        // faccia l'allineamento quando esistono split paese (shipping/billing/target/user).
        if (commonSplits.length === 0) {
          // no-op: mantieni `rows` così com'è
        } else {
          rows.forEach((row) => {
            if (!row || row.length === 0) return;

            // Determina il tipo di connettore della riga
            // Fix: cerca prima nelle metriche, poi negli split (per gestire righe senza split)
            const firstMetric = row.find((cell) => !cell.isSplit);
            const firstSplit = row.find((cell) => cell.isSplit);
            const rowConnectorType = (firstMetric?.type || firstSplit?.type) as
              | ConnectorType
              | undefined;

            // Preserva righe di altri connettori (Google Analytics, ecc.)
            // Ma include Kipify se ha Total Marketing Spend
            // E include Shopify se ha metriche di revenue/sales e ci sono metriche di marketing spend
            // rowConnectorType può essere ConnectorType | MetricsByConnectorType
            const isKipifyType =
              rowConnectorType === ConnectorType.KIPIFY ||
              (rowConnectorType as any) === MetricsByConnectorType.KIPIFY;
            const isKipifyWithTotalSpend =
              isKipifyType && hasKipifyTotalMarketingSpend;
            // Fix: Include Shopify nel join SOLO se ci sono almeno 2 metriche spend
            // (non solo 1, altrimenti tutto viene messo su una riga)
            const spendMetricsCount = [
              hasGoogleAdsSpend,
              hasFacebookSpend,
              hasKipifyTotalMarketingSpend,
            ].filter(Boolean).length;

            const isShopifyWithRevenue =
              rowConnectorType === ConnectorType.SHOPIFY &&
              hasShopifyRevenue &&
              spendMetricsCount >= 2; // Richiede almeno 2 metriche spend per includere Shopify
            const isMarketingSpendConnector =
              rowConnectorType === ConnectorType.GOOGLE_ADS ||
              rowConnectorType === ConnectorType.FACEBOOK ||
              isKipifyWithTotalSpend ||
              isShopifyWithRevenue;

            if (!isMarketingSpendConnector) {
              otherRows.push(row);
              return;
            }

            // Helper: trova la cella di split da usare per il join, gestendo varianti e normalizzazioni
            const findJoinCellForSplit = (splitKey: string) => {
              // match diretto e country swap user/target
              let cell = row.find(
                (c) =>
                  c.isSplit &&
                  (c.id === splitKey ||
                    (splitKey === 'user_country' &&
                      c.id === 'target_country') ||
                    (splitKey === 'target_country' && c.id === 'user_country')),
              );

              // Varianti per product_title
              if (!cell && splitKey === 'product_title') {
                cell = row.find(
                  (c) =>
                    c.isSplit &&
                    (c.id === 'product_title' ||
                      c.id === 'segments.product_title' ||
                      c.id === 'li.title'),
                );
              }

              // Varianti per product_type
              if (!cell && splitKey === 'product_type') {
                cell = row.find(
                  (c) =>
                    c.isSplit &&
                    (c.id === 'product_type' ||
                      c.id === 'segments.product_type_l1'),
                );
              }

              // Alias search term (GAQL vs chiave logica in cella)
              if (!cell && joinSplitNormalizedKey(splitKey) === 'search_term') {
                cell = row.find(
                  (c) =>
                    c.isSplit &&
                    (c.id === 'search_term' ||
                      c.id === 'search_term_view.search_term' ||
                      c.id === 'campaign_search_term_view.search_term'),
                );
              }

              if (!cell && joinSplitNormalizedKey(splitKey) === 'keyword') {
                cell = row.find(
                  (c) =>
                    c.isSplit &&
                    (c.id === 'keyword' ||
                      c.id === 'ad_group_criterion.keyword.text' ||
                      c.id === 'segments.keyword.info.text'),
                );
              }

              return cell;
            };

            // Se abbiamo split comuni, NON includere righe che non li possiedono:
            // altrimenti finiamo per creare bucket "__NO_VALUE__" e righe "a caso" (es. Facebook senza product_type).
            if (commonSplits.length > 0) {
              const hasAllCommonSplits = commonSplits.every((split) => {
                // Caso speciale: kipify_country può derivare da target_region/user_region
                if (split.key === 'kipify_country') {
                  const regionCell =
                    row.find((c) => c.isSplit && c.id === 'target_region') ||
                    row.find((c) => c.isSplit && c.id === 'user_region') ||
                    row.find((c) => c.isSplit && c.id === 'kipify_country');

                  const v =
                    regionCell?.value != null
                      ? String(regionCell.value).trim()
                      : '';
                  return v !== '' && v !== '-';
                }

                const cell = findJoinCellForSplit(split.key);
                const v = cell?.value != null ? String(cell.value).trim() : '';
                return v !== '' && v !== '-';
              });

              if (!hasAllCommonSplits) {
                otherRows.push(row);
                return;
              }
            }

            // Crea chiave di join basata su splits comuni
            let joinKey = 'total'; // Default: riga totale senza splits
            if (commonSplits.length > 0) {
              const splitValues: string[] = [];
              commonSplits.forEach((split) => {
                // Fix: per product_title, cerca anche le varianti (segments.product_title, li.title)
                let cell = findJoinCellForSplit(split.key);

                // Fix: Se stiamo cercando kipify_country (normalizzato da regioni),
                // cerca target_region o user_region e normalizza al nome della regione (senza codice paese)
                // Questo permette di matchare "Lombardia" (Facebook) con "Lombardia (IT)" (Google Ads)
                // Funziona anche per le righe di Kipify che hanno entrambi gli split
                if (!cell && split.key === 'kipify_country') {
                  // Cerca prima target_region (Facebook o Kipify)
                  let regionCell = row.find(
                    (c) => c.isSplit && c.id === 'target_region',
                  );
                  // Se non trovato, cerca user_region (Google Ads o Kipify)
                  if (!regionCell) {
                    regionCell = row.find(
                      (c) => c.isSplit && c.id === 'user_region',
                    );
                  }

                  // Se abbiamo trovato una regione, normalizzala
                  if (regionCell && regionCell.value) {
                    const regionValue = String(regionCell.value);

                    // Normalizza il nome della regione rimuovendo il codice paese se presente
                    // Es: "Lombardia (IT)" -> "lombardia", "Lombardia" -> "lombardia", "Regione 20151" -> "regione 20151"
                    // Questo permette di matchare regioni con e senza codice paese
                    const normalizedRegionName = regionValue
                      .replace(/\s*\([A-Z]{2}\)\s*$/, '')
                      .trim()
                      .toLowerCase();

                    // Usa il nome normalizzato come chiave di join
                    // Questo permette di matchare "Lombardia" (Facebook) con "Lombardia (IT)" (Google Ads) e con le righe di Kipify
                    splitValues.push(`${split.key}:${normalizedRegionName}`);
                    return; // Skip il resto della logica per questo split
                  }

                  // Se non abbiamo trovato una regione ma la riga è di Kipify con split per regioni,
                  // potrebbe essere che Kipify ha già normalizzato gli split in modo diverso
                  // In questo caso, prova a cercare altri split che potrebbero contenere informazioni sulla regione
                  const rowConnectorType = (row.find((cell) => !cell.isSplit)
                    ?.type || row.find((cell) => cell.isSplit)?.type) as
                    | ConnectorType
                    | undefined;
                  const isKipifyRow =
                    rowConnectorType === ConnectorType.KIPIFY ||
                    (rowConnectorType as any) === MetricsByConnectorType.KIPIFY;

                  if (isKipifyRow) {
                    // Per le righe di Kipify, se non abbiamo trovato regioni, potrebbe essere una riga senza split
                    // In questo caso, usa '__NO_VALUE__' per indicare che non c'è una regione specifica
                    splitValues.push(`${split.key}:__NO_VALUE__`);
                    return;
                  }
                }

                // Normalizza il valore per product_type (stessa normalizzazione usata nel join)
                let value =
                  cell?.value != null &&
                  cell.value !== '-' &&
                  String(cell.value).trim() !== ''
                    ? String(cell.value).trim()
                    : '__NO_VALUE__';

                // Applica normalizzazione per product_type (case-insensitive, trim, compatta spazi)
                if (split.key === 'product_type' && value !== '__NO_VALUE__') {
                  value = value
                    .toLowerCase()
                    .replace(/\s{2,}/g, ' ')
                    .trim();
                }

                splitValues.push(
                  `${joinSplitNormalizedKey(split.key)}:${value}`,
                );
              });
              joinKey = splitValues.join('|');
            }

            // Ottieni o crea la mappa delle celle per questa chiave di join
            let cellMap = joinKeyMap.get(joinKey);
            if (!cellMap) {
              cellMap = new Map<string, TableRowsType>();
              joinKeyMap.set(joinKey, cellMap);
            }

            // Aggiungi tutte le celle della riga alla mappa
            // Il join unisce le righe mettendo tutte le celle insieme (Google Ads e Facebook in colonne diverse)
            row.forEach((cell) => {
              if (!cellMap) return; // Type guard

              // Se esiste già una cella con lo stesso columnId, usa quella esistente (non sovrascrivere)
              // Questo può succedere solo se due righe hanno la stessa colonna, ma normalmente
              // Google Ads e Facebook hanno columnId diversi, quindi vengono semplicemente aggiunte
              if (!cellMap.has(cell.columnId)) {
                cellMap.set(cell.columnId, cell);
              }
            });
          });

          // Ricostruisci le righe dalla mappa e aggiungi le altre righe
          const joinedRows = Array.from(joinKeyMap.values()).map((cellMap) =>
            Array.from(cellMap.values()),
          );
          rows = [...joinedRows, ...otherRows];

          const hasRoasMetric = selectedMetrics.some(
            (m) =>
              (m.type === ConnectorType.KIPIFY ||
                m.type === MetricsByConnectorType.KIPIFY) &&
              (m.key === 'roas' || m.id === 'kipify_roas'),
          );

          // Ricalcola ROAS (Kipify) quando l'utente lo seleziona e abbiamo sia Revenue che Spend.
          // Serve soprattutto quando facciamo join cross-connector (es. Shopify Vendor + Google Ads Brand + Spend),
          // altrimenti la colonna ROAS resta '-' perché non esiste una metrica raw per quella riga.
          if (hasRoasMetric) {
            // Parsing numerico robusto (supporta formato IT "1.234,56" e simboli come "€").
            const parseNumericForRoas = (raw: unknown): number | null => {
              if (raw === null || raw === undefined) return null;
              const s = String(raw).trim();
              if (!s || s === '-') return null;

              let cleaned = s.replace(/[^\d,.-]/g, '');
              if (!cleaned) return null;

              const hasComma = cleaned.includes(',');
              const hasDot = cleaned.includes('.');
              if (hasComma && hasDot) {
                cleaned = cleaned.replace(/\./g, '').replace(/,/g, '.');
              } else if (hasComma && !hasDot) {
                cleaned = cleaned.replace(/,/g, '.');
              }
              const n = Number(cleaned);
              return Number.isFinite(n) ? n : null;
            };

            // Calcola ROAS per-riga cercando direttamente le celle revenue/spend presenti nella riga,
            // senza dipendere da key/columnId (che per Google Ads può essere "spend" vs "metrics.cost_micros").
            const isRevenueCell = (cell: TableRowsType) => {
              if (cell.isSplit) return false;
              if (cell.type !== ConnectorType.SHOPIFY) return false;
              const hay = `${cell.id ?? ''} ${cell.title ?? ''}`;
              return /gross.*sales|total.*sales|net.*sales|revenue|sales/i.test(
                hay,
              );
            };
            const isSpendCell = (cell: TableRowsType) => {
              if (cell.isSplit) return false;
              const hay = `${cell.id ?? ''} ${cell.title ?? ''}`;
              if (cell.type === ConnectorType.FACEBOOK) {
                return /spend|amount_spent|amount/i.test(hay);
              }
              if (cell.type === ConnectorType.GOOGLE_ADS) {
                return /spend|cost|cost_micros|advertising_spend/i.test(hay);
              }
              if (
                cell.type === ConnectorType.KIPIFY ||
                (cell.type as any) === MetricsByConnectorType.KIPIFY
              ) {
                return /total_marketing_spend/i.test(hay);
              }
              return false;
            };

            rows = rows.map((row) => {
              let revenue = 0;
              let totalSpend = 0;
              let hasAnyBase = false;

              row.forEach((cell) => {
                if (!cell || cell.isSplit) return;
                if (isRevenueCell(cell)) {
                  const n = parseNumericForRoas(cell.value);
                  if (n !== null) {
                    revenue += n;
                    hasAnyBase = true;
                  }
                } else if (isSpendCell(cell)) {
                  const n = parseNumericForRoas(cell.value);
                  if (n !== null) {
                    totalSpend += n;
                    hasAnyBase = true;
                  }
                }
              });

              if (!hasAnyBase) return row;

              const roasCellIndex = row.findIndex(
                (cell) =>
                  !cell.isSplit &&
                  (cell.id === 'roas' || cell.id === 'kipify_roas') &&
                  (cell.type === ConnectorType.KIPIFY ||
                    (cell.type as any) === MetricsByConnectorType.KIPIFY),
              );

              const existingRoasValue =
                roasCellIndex >= 0 ? row[roasCellIndex]?.value : undefined;
              const hasExistingRoasValue =
                existingRoasValue !== null &&
                existingRoasValue !== undefined &&
                String(existingRoasValue).trim() !== '' &&
                String(existingRoasValue).trim() !== '-';

              let roasValue: string | number = hasExistingRoasValue
                ? String(existingRoasValue)
                : '-';
              if (!hasExistingRoasValue) {
                if (revenue > 0 && totalSpend > 0) {
                  const roas = revenue / totalSpend;
                  roasValue = isFinite(roas) ? roas.toFixed(2) : '-';
                } else if (revenue === 0 && totalSpend > 0) {
                  roasValue = '0.00';
                } else {
                  roasValue = '-';
                }
              }

              if (roasCellIndex >= 0) {
                row[roasCellIndex].value = roasValue;
                row[roasCellIndex].id = 'roas';
                if (!row[roasCellIndex].icon) {
                  row[roasCellIndex].icon = getIconForMetric(
                    MetricsByConnectorType.KIPIFY,
                  );
                }
              } else {
                const roasMetric = selectedMetrics.find(
                  (m) =>
                    (m.type === ConnectorType.KIPIFY ||
                      m.type === MetricsByConnectorType.KIPIFY) &&
                    (m.key === 'roas' || m.id === 'kipify_roas'),
                );

                if (roasMetric) {
                  row.push({
                    id: 'roas',
                    title: roasMetric.label || 'Roas',
                    value: roasValue,
                    type:
                      (roasMetric.type as any) || MetricsByConnectorType.KIPIFY,
                    columnId: buildColumnId(
                      'roas',
                      (roasMetric.type as any) || MetricsByConnectorType.KIPIFY,
                    ),
                    isSplit: false,
                    icon: getIconForMetric(MetricsByConnectorType.KIPIFY),
                  });
                }
              }

              return row;
            });
          }
        }
      }

      // Gestione speciale per kipify_country
      if (hasKipifyCountry) {
        rows = groupRowsByKipifyCountry(rows);
      }

      // Create updated rows with consistent structure
      let updatedRows = rows.map((row) => {
        const visibleCells = uniqTableRow.map((val) => {
          const foundRow = row.find(({ columnId }) =>
            cellsMatchColumnId(val.columnId, columnId),
          );
          // Stesso columnId del catalogo/form: altrimenti drag usa ad_network mentre
          // columnOrder ha segments.ad_network_type → drop a sinistra non funziona.
          if (!foundRow) return { ...val, value: '-' };
          return {
            ...foundRow,
            columnId: val.columnId,
            id: val.id,
            title: val.title || foundRow.title,
            isSplit: val.isSplit,
            type: val.type,
            icon: val.icon || foundRow.icon,
          };
        });

        const hiddenMetricHelpers = row.filter(
          (cell) =>
            !cell.isSplit &&
            !visibleCells.some((visibleCell) =>
              cellsMatchColumnId(visibleCell.columnId, cell.columnId),
            ),
        );

        return sortBySplitsAndMetrics([
          ...visibleCells,
          ...hiddenMetricHelpers,
        ]);
      });

      // Ricalcola ROAS (Kipify) per-riga *sempre* quando selezionato, anche se non ci sono "commonSplits".
      // Nel caso Vendor (Shopify) + Product Brand (Google Ads) lo join usa un mapping dedicato
      // e spesso commonSplits è vuoto: se leghiamo il calcolo ROAS alla logica commonSplits, ROAS resta "-".
      const hasRoasMetric = selectedMetrics.some(
        (m) =>
          (m.type === ConnectorType.KIPIFY ||
            m.type === MetricsByConnectorType.KIPIFY) &&
          (m.key === 'roas' || m.id === 'kipify_roas'),
      );

      if (hasRoasMetric) {
        const parseNumericForRoas = (raw: unknown): number | null => {
          if (raw === null || raw === undefined) return null;
          const s = String(raw).trim();
          if (!s || s === '-') return null;

          let cleaned = s.replace(/[^\d,.-]/g, '');
          if (!cleaned) return null;

          const hasComma = cleaned.includes(',');
          const hasDot = cleaned.includes('.');
          // Se compaiono sia "." che ",", assumiamo formato IT (punti migliaia, virgola decimali)
          if (hasComma && hasDot) {
            cleaned = cleaned.replace(/\./g, '').replace(/,/g, '.');
          } else if (hasComma && !hasDot) {
            cleaned = cleaned.replace(/,/g, '.');
          }
          const n = Number(cleaned);
          return Number.isFinite(n) ? n : null;
        };

        const isRevenueCell = (cell: TableRowsType) => {
          if (cell.isSplit) return false;
          if (cell.type !== ConnectorType.SHOPIFY) return false;
          const hay = `${cell.id ?? ''} ${cell.title ?? ''}`;
          return /gross.*sales|total.*sales|net.*sales|revenue|sales/i.test(
            hay,
          );
        };

        const isSpendCell = (cell: TableRowsType) => {
          if (cell.isSplit) return false;
          const hay = `${cell.id ?? ''} ${cell.title ?? ''}`;
          if (cell.type === ConnectorType.FACEBOOK) {
            return /spend|amount_spent|amount/i.test(hay);
          }
          if (cell.type === ConnectorType.GOOGLE_ADS) {
            return /spend|cost|cost_micros|advertising_spend/i.test(hay);
          }
          if (
            cell.type === ConnectorType.KIPIFY ||
            (cell.type as any) === MetricsByConnectorType.KIPIFY
          ) {
            return /total_marketing_spend/i.test(hay);
          }
          return false;
        };

        updatedRows = updatedRows.map((row) => {
          let revenue = 0;
          let totalSpend = 0;
          let hasAnyBase = false;

          row.forEach((cell) => {
            if (!cell || cell.isSplit) return;
            if (isRevenueCell(cell)) {
              const n = parseNumericForRoas(cell.value);
              if (n !== null) {
                revenue += n;
                hasAnyBase = true;
              }
            } else if (isSpendCell(cell)) {
              const n = parseNumericForRoas(cell.value);
              if (n !== null) {
                totalSpend += n;
                hasAnyBase = true;
              }
            }
          });

          if (!hasAnyBase) return row;

          const existingRoasCell = row.find(
            (cell) =>
              !cell.isSplit &&
              (cell.id === 'roas' || cell.id === 'kipify_roas') &&
              (cell.type === ConnectorType.KIPIFY ||
                (cell.type as any) === MetricsByConnectorType.KIPIFY),
          );
          const hasExistingRoasValue =
            existingRoasCell?.value !== null &&
            existingRoasCell?.value !== undefined &&
            String(existingRoasCell.value).trim() !== '' &&
            String(existingRoasCell.value).trim() !== '-';

          let roasValue: string | number = hasExistingRoasValue
            ? String(existingRoasCell?.value)
            : '-';
          if (!hasExistingRoasValue) {
            if (revenue > 0 && totalSpend > 0) {
              const roas = revenue / totalSpend;
              roasValue = isFinite(roas) ? roas.toFixed(2) : '-';
            } else if (revenue === 0 && totalSpend > 0) {
              roasValue = '0.00';
            }
          }

          return row.map((cell) => {
            if (
              !cell.isSplit &&
              (cell.id === 'roas' || cell.id === 'kipify_roas') &&
              (cell.type === ConnectorType.KIPIFY ||
                (cell.type as any) === MetricsByConnectorType.KIPIFY)
            ) {
              return { ...cell, id: 'roas', value: roasValue };
            }
            return cell;
          });
        });
      }

      return updatedRows;
    },
    [methods],
  );

  const downloadCSV = () => {
    // Stessa logica di tableIsShown: consentire download se c'è almeno una fonte con dati (incluso GA4)
    const hasDataToExport = !!(
      googleAds?.getGoogleAdsMetrics?.length ||
      shopifyMetricValues?.getShopifyMetricValues?.metricRows?.length ||
      facebookMetricsValues?.getFacebookMetricsValues?.length ||
      googleAnalyticsMetricValues?.getGoogleAnalytics4Metrics?.length ||
      googleSearchConsoleMetricValues?.getGoogleSearchConsoleMetricValues
        ?.length ||
      initData?.rows?.googleAds?.length ||
      initData?.rows?.googleAnalytics4?.length ||
      initData?.rows?.facebook?.length ||
      initData?.rows?.shopify?.length ||
      kipifyMetricsValues?.getKipifyMetricsValues?.length ||
      initData?.rows?.kipify?.length ||
      customFormulaValues?.getCustomFormulaValues?.length ||
      initData?.rows?.customFormula?.length ||
      databaseMetricValues?.getDatabaseConnectorDataForCustomReport?.length ||
      initData?.rows?.database?.length ||
      magentoMetricValues?.getMagentoMetricValues?.metricRows?.length
    );
    if (!hasDataToExport) return;

    const tableRows = getTableRows({
      googleAdsData: googleAds?.getGoogleAdsMetrics ?? initData?.rows.googleAds,
      shopifyData:
        shopifyMetricValues?.getShopifyMetricValues.metricRows ??
        initData?.rows.shopify,
      magentoData: magentoMetricValues?.getMagentoMetricValues.metricRows,
      facebookData:
        facebookMetricsValues?.getFacebookMetricsValues ??
        initData?.rows.facebook,
      googleAnalyticsData:
        googleAnalyticsMetricValues?.getGoogleAnalytics4Metrics ??
        initData?.rows.googleAnalytics4,
      googleSearchConsoleData:
        googleSearchConsoleMetricValues?.getGoogleSearchConsoleMetricValues,
      kipifyData:
        (kipifyMetricsValues?.getKipifyMetricsValues as
          | MetricValuesRow[]
          | undefined) ?? initData?.rows.kipify,
      customFormulaData: customFormulaValues?.getCustomFormulaValues,
      databaseData:
        databaseMetricValues?.getDatabaseConnectorDataForCustomReport ??
        initData?.rows?.database ??
        [],
    });

    // Keep column order as defined by user via drag and drop
    const newTableRows = tableRows.map((row) => {
      // Order rows according to column order defined by user
      const orderedRow = tableColumnOrder
        .map((colId) => row.find((cell) => cell.columnId === colId))
        .filter(Boolean);
      return orderedRow;
    });

    // Dati di confronto per includere colonne (confronto) e (variazione %) nel CSV
    const csvCompareData =
      compareDateRange &&
      (compareShopifyMetricValues?.getShopifyMetricValues ||
        compareMagentoMetricValues?.getMagentoMetricValues ||
        compareFacebookMetricValues?.getFacebookMetricsValues ||
        compareGoogleAdsMetricValues?.getGoogleAdsMetrics ||
        compareGoogleAnalyticsMetricValues?.getGoogleAnalytics4Metrics ||
        compareGoogleSearchConsoleMetricValues?.getGoogleSearchConsoleMetricValues ||
        compareKipifyMetricValues?.getKipifyMetricsValues ||
        compareCustomFormulaValues?.getCustomFormulaValues ||
        compareDatabaseMetricValues?.getDatabaseConnectorDataForCustomReport)
        ? {
            shopify: compareShopifyMetricValues?.getShopifyMetricValues ?? null,
            magento: compareMagentoMetricValues?.getMagentoMetricValues ?? null,
            facebook:
              compareFacebookMetricValues?.getFacebookMetricsValues ?? null,
            googleAds:
              compareGoogleAdsMetricValues?.getGoogleAdsMetrics ?? null,
            googleAnalytics4:
              compareGoogleAnalyticsMetricValues?.getGoogleAnalytics4Metrics ??
              null,
            googleSearchConsole:
              compareGoogleSearchConsoleMetricValues?.getGoogleSearchConsoleMetricValues ??
              null,
            kipify: compareKipifyMetricValues?.getKipifyMetricsValues ?? null,
            customFormula:
              compareCustomFormulaValues?.getCustomFormulaValues ?? null,
            database:
              compareDatabaseMetricValues?.getDatabaseConnectorDataForCustomReport ??
              null,
          }
        : null;

    const tableData = newTableRows.map((row, idx) => {
      const dataRow: { [key: string]: string | number } = { '№': idx + 1 };
      const rowCells = row.filter((c): c is TableRowsType => !!c);

      row.forEach((cell) => {
        if (cell && cell.title && cell.value !== undefined) {
          // Applica la stessa formattazione usata nella visualizzazione della tabella
          let formattedValue: string | number = cell.value;

          // Se il valore è numerico, applica la formattazione corretta
          if (!isNaN(Number(cell.value))) {
            const cellType = methods
              .watch('metrics')
              .find((metric) => metric.key === cell.id)?.valueType;

            formattedValue = formatCustomReportCellValue({
              value: cell.value,
              connectorType: cell.type,
              isSplit: cell.isSplit,
              cellType,
            });
          }

          dataRow[cell.title] = formattedValue;

          // Colonne comparazione: valore periodo confronto e variazione %
          if (csvCompareData && !cell.isSplit && !isNaN(Number(cell.value))) {
            const compareVal = findCompareValue(cell, rowCells, csvCompareData);
            if (compareVal !== null && !isNaN(compareVal)) {
              const cellType = methods
                .watch('metrics')
                .find((metric) => metric.key === cell.id)?.valueType;
              const compareFormatted = formatCustomReportCellValue({
                value: compareVal,
                connectorType: cell.type,
                isSplit: cell.isSplit,
                cellType,
              });
              dataRow[`${cell.title} (confronto)`] = compareFormatted;
              const change = calculateChange(
                Number(cell.value),
                compareVal,
                cell.id,
                cell.type,
              );
              if (change) {
                dataRow[`${cell.title} (variazione %)`] =
                  'isInfinite' in change && change.isInfinite
                    ? (change.isPositive ? '+' : '-') + '∞'
                    : (change.isPositive ? '+' : '') + `${change.percentage}%`;
              }
            }
          }
        }
      });

      return dataRow;
    });

    const csv = unparse(tableData);

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${title}.csv`);
    link.click();
  };

  const tableIsShown = !!(
    googleAds?.getGoogleAdsMetrics?.length ||
    shopifyMetricValues?.getShopifyMetricValues.metricRows.length ||
    facebookMetricsValues?.getFacebookMetricsValues?.length ||
    googleAnalyticsMetricValues?.getGoogleAnalytics4Metrics?.length ||
    googleSearchConsoleMetricValues?.getGoogleSearchConsoleMetricValues
      ?.length ||
    initData?.rows.googleAds?.length ||
    initData?.rows.googleAnalytics4?.length ||
    initData?.rows.facebook?.length ||
    initData?.rows.shopify?.length ||
    kipifyMetricsValues?.getKipifyMetricsValues?.length ||
    initData?.rows?.kipify?.length ||
    customFormulaValues?.getCustomFormulaValues?.length ||
    initData?.rows.customFormula?.length ||
    databaseMetricValues?.getDatabaseConnectorDataForCustomReport?.length ||
    initData?.rows?.database?.length ||
    magentoMetricValues?.getMagentoMetricValues?.metricRows?.length
  );

  const areMetricValuesLoading =
    googleAdsLoading ||
    shopifyLoading ||
    magentoLoading ||
    facebookMetricsValuesLoading ||
    googleAnalticsLoading ||
    googleSearchConsoleLoading ||
    customFormulaValuesLoading ||
    kipifyMetricsValuesLoading ||
    databaseLoading;

  // More robust loading state that also considers debounce
  const isDebouncing =
    debouncedMetrics !== methods.watch('metrics') ||
    debouncedSplits !== methods.watch('splits') ||
    debouncedFilters !== methods.watch('filters') ||
    debouncedStartDate !== methods.watch('startDate') ||
    debouncedEndDate !== methods.watch('endDate');

  // I commented this data because it prevented removing predefined metrics and splits
  const originalGoogleAdsData = googleAds?.getGoogleAdsMetrics; // ?? initData?.rows.googleAds;
  // Usa i dati originali
  const googleAdsData = originalGoogleAdsData;

  const isTableLoading =
    areMetricValuesLoading || isDebouncing || googleAdsLoading;
  const shopifyData = shopifyMetricValues?.getShopifyMetricValues.metricRows; //  ?? initData?.rows.shopify;
  const magentoData = magentoMetricValues?.getMagentoMetricValues?.metricRows;
  const facebookData = facebookMetricsValues?.getFacebookMetricsValues; //  ?? initData?.rows.facebook;
  const googleAnalyticsData =
    googleAnalyticsMetricValues?.getGoogleAnalytics4Metrics; // ?? initData?.rows.googleAnalytics4;
  // Cap FE: oltre ~500 righe il compare O(n²) congela il tab (stesso problema Facebook).
  const googleSearchConsoleData = (
    googleSearchConsoleMetricValues?.getGoogleSearchConsoleMetricValues || []
  ).slice(0, 500);
  const compareGoogleSearchConsoleData = (
    compareGoogleSearchConsoleMetricValues?.getGoogleSearchConsoleMetricValues ||
    []
  ).slice(0, 500);
  const kipifyData =
    (kipifyMetricsValues?.getKipifyMetricsValues as
      | MetricValuesRow[]
      | undefined) ?? initData?.rows?.kipify;

  const kipifyDataForTable = useMemo(() => {
    if (!Array.isArray(kipifyData)) return kipifyData;
    // Le righe helper `_is_total_row` servono solo alla riga "Total" del frontend.
    // Se restano nel dataset principale finiscono nel body tabella o falsano i calcoli.
    return kipifyData.filter(
      (r) =>
        !(r.splitValues || []).some(
          (sv) => sv && sv.key === '_is_total_row' && sv.value === 'true',
        ),
    );
  }, [kipifyData]);
  const customFormulaData = customFormulaValues?.getCustomFormulaValues; //  ?? initData?.rows.customFormula;
  const rawDatabaseRows =
    databaseMetricValues?.getDatabaseConnectorDataForCustomReport ?? [];
  const databaseData = databaseConnectorId ? rawDatabaseRows : [];

  const rows = useMemo(() => {
    return (
      (!isTableLoading &&
        tableIsShown &&
        getTableRows({
          googleAdsData,
          shopifyData,
          magentoData,
          facebookData,
          googleAnalyticsData,
          googleSearchConsoleData,
          kipifyData: kipifyDataForTable,
          customFormulaData,
          databaseData,
        })) ||
      null
    );
  }, [
    isTableLoading,
    customFormulaData,
    facebookData,
    getTableRows,
    googleAdsData,
    googleAnalyticsData,
    googleSearchConsoleData,
    shopifyData,
    magentoData,
    databaseData,
    kipifyMetricsValues?.getKipifyMetricsValues,
    kipifyDataForTable,
    tableIsShown,
  ]);

  // Fallback: garantisco che rows sia sempre un array di array (anche se vuoto)
  const safeRows = useMemo(() => {
    // Trova tutti gli splits selezionati
    const selectedSplits = methods.watch(InputName.splits) || [];
    // Trova tutti i metrics selezionati
    const selectedMetrics = methods.watch(InputName.metrics) || [];
    // Se non ci sono righe, crea una riga vuota con tutte le colonne split/metric selezionate
    if (!Array.isArray(rows) || rows.length === 0 || !Array.isArray(rows[0])) {
      // Crea colonne per tutti gli splits e metrics selezionati
      const emptyRow = [
        ...selectedSplits.map((split) => ({
          id: split.key,
          columnId: buildColumnId(split.key, split.connectorType),
          title: split.label || split.key,
          type: split.connectorType,
          value: '-',
          isSplit: true,
        })),
        ...selectedMetrics.map((metric) => ({
          id: metric.key,
          columnId: buildColumnId(metric.key, metric.type),
          title: metric.label || metric.key,
          type: metric.type,
          value: '-',
          isSplit: false,
        })),
      ];
      return [emptyRow];
    }
    // Se ci sono righe, assicurati che tutte le colonne split/metric selezionate siano presenti
    return rows.map((row) => {
      const rowColumnIds = row.map((col) => col.columnId);
      const selectedSplitsForRow = methods.watch(InputName.splits) || [];
      const selectedMetricsForRow = methods.watch(InputName.metrics) || [];
      // Usa cellsMatchColumnId: l'API usa chiavi logiche (es. search_term) il catalogo GAQL (es. search_term_view.search_term)
      const missingSplits = selectedSplitsForRow.filter(
        (split) =>
          !row.some(
            (col) =>
              col.isSplit &&
              cellsMatchColumnId(
                buildColumnId(split.key, split.connectorType),
                col.columnId,
              ),
          ),
      );
      const missingMetrics = selectedMetricsForRow.filter(
        (metric) =>
          !row.some(
            (col) =>
              !col.isSplit &&
              cellsMatchColumnId(
                buildColumnId(metric.key, metric.type),
                col.columnId,
              ),
          ),
      );
      return [
        ...row,
        ...missingSplits.map((split) => ({
          id: split.key,
          columnId: buildColumnId(split.key, split.connectorType),
          title: split.label || split.key,
          type: split.connectorType,
          value: '-',
          isSplit: true,
        })),
        ...missingMetrics.map((metric) => ({
          id: metric.key,
          columnId: buildColumnId(metric.key, metric.type),
          title: metric.label || metric.key,
          type: metric.type,
          value: '-',
          isSplit: false,
        })),
      ];
    });
  }, [rows, methods]);

  const selectedMetrics = methods.watch(InputName.metrics);
  const selectedSplits = methods.watch(InputName.splits);

  const splitColumnIds = useMemo(
    () =>
      (selectedSplits || []).map((split) =>
        buildColumnId(split.key, split.connectorType),
      ),
    [selectedSplits],
  );

  const metricColumnIds = useMemo(
    () =>
      (selectedMetrics || []).map((metric) =>
        buildColumnId(metric.key, metric.type),
      ),
    [selectedMetrics],
  );

  const missedMetricColumnIds = useMemo(
    () =>
      metricColumnIds.filter(
        (columnId) =>
          !tableColumnOrder.some((id) => cellsMatchColumnId(columnId, id)),
      ),
    [metricColumnIds, tableColumnOrder],
  );

  const missedSplitColumnIds = useMemo(
    () =>
      splitColumnIds.filter(
        (columnId) =>
          !tableColumnOrder.some((id) => cellsMatchColumnId(columnId, id)),
      ),
    [splitColumnIds, tableColumnOrder],
  );

  useEffect(() => {
    if (missedMetricColumnIds.length || missedSplitColumnIds.length) {
      setTableColumnOrder((prevOrder) => {
        /**
         * IMPORTANTE (Custom Report):
         * L'utente può riordinare liberamente colonne split e metriche. Non dobbiamo mai
         * "normalizzare" in automatico con logiche tipo "splits first", perché dopo refresh/save
         * sembra che l'ordine non venga salvato.
         *
         * Qui preserviamo SEMPRE l'ordine esistente e al massimo appendiamo in coda le nuove colonne.
         * Se l'ordine è vuoto (prima init), usiamo l'ordine corrente delle colonne in `safeRows[0]`
         * come default deterministico.
         */

        const filteredPrev = prevOrder.filter(
          (id) =>
            !missedMetricColumnIds.includes(id) &&
            !missedSplitColumnIds.includes(id),
        );

        const baseOrder: string[] =
          filteredPrev.length > 0
            ? filteredPrev
            : Array.isArray(safeRows) && Array.isArray(safeRows[0])
              ? safeRows[0]
                  .map((col) => col?.columnId)
                  .filter(
                    (v): v is string => typeof v === 'string' && v.length > 0,
                  )
              : [];

        // Aggiungi automaticamente eventuali split Facebook presenti nei dati ma non ancora nell'ordine colonne
        const facebookSplitIdsFromRows =
          Array.isArray(safeRows) && Array.isArray(safeRows[0])
            ? safeRows[0]
                .filter(
                  (col) =>
                    col &&
                    col.isSplit &&
                    col.type === ConnectorType.FACEBOOK &&
                    typeof col.columnId === 'string',
                )
                .map((col) => col.columnId)
            : [];

        const missingIdsInDeterministicOrder = [
          ...missedSplitColumnIds,
          ...facebookSplitIdsFromRows.filter(
            (id) => !baseOrder.some((existing) => cellsMatchColumnId(existing, id)),
          ),
          ...missedMetricColumnIds,
        ];

        const nextOrder: string[] = [...baseOrder];
        missingIdsInDeterministicOrder.forEach((id) => {
          if (!nextOrder.some((existing) => cellsMatchColumnId(existing, id))) {
            nextOrder.push(id);
          }
        });

        return nextOrder;
      });
    }
  }, [
    missedMetricColumnIds,
    missedSplitColumnIds,
    splitColumnIds,
    metricColumnIds,
    safeRows,
  ]);

  const hasData = !!(tableIsShown && rows && rows.length);

  // Forza il salvataggio di dateRange in config ogni volta che si salva il report
  function normalizeConfig(config: any) {
    // Serializzazione sicura delle date con validazione
    // IMPORTANTE: il backend gestisce il timezone dello shop, quindi inviamo solo YYYY-MM-DD
    // Il backend convertirà automaticamente in UTC usando il timezone dello shop
    const safeToDateOnly = (raw?: string) => {
      if (!raw) return undefined;
      try {
        // Estrai solo YYYY-MM-DD (ignora timezone e ora)
        const dateOnly = raw.split('T')[0];
        const match = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);

        if (match) {
          const [, yearStr, monthStr, dayStr] = match;
          const year = parseInt(yearStr, 10);
          const month = parseInt(monthStr, 10);
          const day = parseInt(dayStr, 10);

          if (
            !isNaN(year) &&
            !isNaN(month) &&
            !isNaN(day) &&
            year >= 2000 &&
            year <= 2100 &&
            month >= 1 &&
            month <= 12 &&
            day >= 1 &&
            day <= 31
          ) {
            // Ritorna solo YYYY-MM-DD, il backend farà la conversione timezone
            return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          }
        }

        // Fallback: prova con new Date() se non riusciamo a estrarre la data
        const date = new Date(raw);
        if (isNaN(date.getTime())) {
          return undefined;
        }
        // Estrai solo YYYY-MM-DD anche dal fallback
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        if (year < 2000 || year > 2100) return undefined;
        return `${year}-${month}-${day}`;
      } catch (error) {
        return undefined;
      }
    };

    let dateRange;
    if (config.dateRange) {
      const from = safeToDateOnly(config.dateRange.from);
      const to = safeToDateOnly(config.dateRange.to);
      if (from || to) {
        dateRange = { from, to };
      }
    } else {
      // Se non c'è, prendi i valori attuali dei picker (sono già in formato YYYY-MM-DD)
      const startDate = methods?.watch?.('startDate');
      const endDate = methods?.watch?.('endDate');
      dateRange = {
        from: startDate ? safeToDateOnly(startDate) : undefined,
        to: endDate ? safeToDateOnly(endDate) : undefined,
      };
    }

    const promptSnapshotRows = Array.isArray(rows)
      ? rows.map((rowArr) =>
          Array.isArray(rowArr)
            ? rowArr.map((cell) => ({
                columnId: cell.columnId,
                title: cell.title,
                type: cell.type,
                value: cell.value,
                isSplit: cell.isSplit,
              }))
            : [],
        )
      : [];

    // Converte le righe della tabella nel formato MetricValuesRowsGroupedByConnector
    const metricValuesRowsGroupedByConnector: Record<string, any[]> = {};
    if (rows && Array.isArray(rows) && rows.length > 0) {
      // Raggruppa le celle per connettore
      const cellsByConnector: Record<string, any[]> = {};
      rows.forEach((rowArr) => {
        rowArr.forEach((cell) => {
          if (!cellsByConnector[cell.type]) {
            cellsByConnector[cell.type] = [];
          }
          cellsByConnector[cell.type].push(cell);
        });
      });

      // Converte in formato MetricValuesRowsGroupedByConnector
      Object.entries(cellsByConnector).forEach(([connectorType, cells]) => {
        // Filtra kipify e kipify_normalized - non sono supportati da GraphQL
        if (
          connectorType === ConnectorType.KIPIFY ||
          connectorType === MetricsByConnectorType.KIPIFY ||
          connectorType === 'kipify' ||
          connectorType === 'kipify_normalized'
        ) {
          return;
        }

        const metricValues = cells
          .filter((cell) => !cell.isSplit)
          .map((cell) => ({ key: cell.id, value: cell.value }));

        const splitValues = cells
          .filter((cell) => cell.isSplit)
          .map((cell) => ({ key: cell.id, value: cell.value }));

        if (metricValues.length > 0 || splitValues.length > 0) {
          metricValuesRowsGroupedByConnector[connectorType] = [
            {
              metricValues,
              splitValues,
            },
          ];
        }
      });
    }

    // Filtra esplicitamente kipify e kipify_normalized anche se presenti in config esistente
    const validConnectors = [
      ConnectorType.SHOPIFY,
      ConnectorType.FACEBOOK,
      ConnectorType.GOOGLE_ADS,
      ConnectorType.GOOGlE_ANALYTICS4,
      ConnectorType.DATABASE,
      MetricsByConnectorType.CUSTOM_FORMULA,
    ];
    const filteredMetricValuesRowsGroupedByConnector: Record<string, any[]> =
      {};
    Object.entries(metricValuesRowsGroupedByConnector).forEach(
      ([key, value]) => {
        // Filtra esplicitamente kipify e tutte le sue varianti
        if (
          key === ConnectorType.KIPIFY ||
          key === MetricsByConnectorType.KIPIFY ||
          key === 'kipify' ||
          key === 'kipify_normalized'
        ) {
          return;
        }
        if (validConnectors.includes(key as any)) {
          filteredMetricValuesRowsGroupedByConnector[key] = value;
        }
      },
    );

    // Filtra anche da config esistente se presente
    if (
      config.metricValuesRowsGroupedByConnector &&
      typeof config.metricValuesRowsGroupedByConnector === 'object'
    ) {
      Object.entries(config.metricValuesRowsGroupedByConnector).forEach(
        ([key, value]) => {
          // Se è kipify, non aggiungerlo
          if (
            key === ConnectorType.KIPIFY ||
            key === MetricsByConnectorType.KIPIFY ||
            key === 'kipify' ||
            key === 'kipify_normalized'
          ) {
            return;
          }
          // Se non è già presente e è valido, aggiungilo
          if (
            validConnectors.includes(key as any) &&
            !filteredMetricValuesRowsGroupedByConnector[key]
          ) {
            filteredMetricValuesRowsGroupedByConnector[key] = value as any;
          }
        },
      );
    }

    // Ottieni i valori attuali dal form per garantire completezza
    const currentSplits = methods?.watch?.('splits') || [];
    const currentMetrics = methods?.watch?.('metrics') || [];
    const currentFilters = methods?.watch?.('filters') || [];

    return {
      ...config,
      dateRange, // forza sempre la presenza di dateRange
      promptSnapshot: {
        rows: promptSnapshotRows,
        columnOrder:
          tableColumnOrder.length > 0 ? tableColumnOrder : config.columnOrder,
        savedAt: new Date().toISOString(),
      },
      metricValuesRowsGroupedByConnector:
        filteredMetricValuesRowsGroupedByConnector,
      splits: currentSplits, // include sempre gli splits attuali
      metrics: currentMetrics, // include sempre le metrics attuali
      filters: currentFilters, // include sempre i filtri attuali
      columnOrder:
        tableColumnOrder.length > 0 ? tableColumnOrder : config.columnOrder, // salva l'ordine delle colonne
    };
  }

  const handleSaveReport = async () => {
    try {
      const config = normalizeConfig({
        dateRange: {
          from: methods.watch('startDate'),
          to: methods.watch('endDate'),
        },
      });

      await createCustomReport({
        variables: {
          input: {
            name: title,
            config,
          },
        },
      });

      snackbar('Report saved successfully', 'success');
      // Il backend gestisce il timezone dello shop, quindi inviamo solo YYYY-MM-DD
      const startDateStr = methods.watch('startDate'); // YYYY-MM-DD
      const endDateStr = methods.watch('endDate'); // YYYY-MM-DD

      onCloseTable({
        dateRange: {
          from: startDateStr || '',
          to: endDateStr || '',
        },
        metricValuesArgsGroupedByConnector: {
          dateRange: {
            from: startDateStr || '',
            to: endDateStr || '',
          },
          splits: [],
        },
        metricValuesRowsGroupedByConnector: {},
      });
    } catch (e) {
      console.error('Error saving report:', e);
      const errorMessage = e instanceof Error ? e.message : 'Unknown error';
      snackbar(`Error saving report: ${errorMessage}`, 'error');
    }
  };

  // Notifica il parent ogni volta che la configurazione cambia
  useEffect(() => {
    if (onConfigChange) {
      const config = normalizeConfig(methods.getValues());
      // Assicurati che columnOrder sia sempre incluso nel config
      const configWithColumnOrder = {
        ...config,
        columnOrder:
          tableColumnOrder.length > 0 ? tableColumnOrder : config.columnOrder,
        // Persisti anche il sort (ordine righe) per evitare reset dopo refresh/save.
        sortConfig: tableSortConfig,
      };
      onConfigChange(configWithColumnOrder);
    }
  }, [methods, onConfigChange, tableColumnOrder, tableSortConfig]);

  return (
    <Wrapper>
      <FormProvider {...methods}>
        <DashboardHeader
          isSmallScreen={isSmallScreen}
          title={title}
          onTitleChange={onTitleChange}
          titleInputRef={titleInputRef}
          onTitleSave={onTitleSave}
          showTitleSaved={showTitleSaved}
          googleAdsMetrics={googleAdsMetrics}
          shopifyFilterValues={
            shopifyMetricValues?.getShopifyMetricValues.filtersOptions || []
          }
          magentoFilterValues={
            magentoMetricValues?.getMagentoMetricValues.filtersOptions || []
          }
          connectors={{
            shopifyConnectorId,
            magentoConnectorId,
            magentoConnectorEnabled,
            facebookConnectorId,
            googleAdsConnectorId,
            googleAnalytics4ConnectorId,
            googleSearchConsoleConnectorId,
          }}
          onBackButtonClick={handleCloseTable}
        />
        <FiltersBlock
          shopifyFilterValues={
            shopifyMetricValues?.getShopifyMetricValues.filtersOptions || []
          }
          magentoFilterValues={
            magentoMetricValues?.getMagentoMetricValues.filtersOptions || []
          }
          metricsIsShown={metricsIsShown}
          closeMetrics={handleCloseMetricsClick}
          showMetrics={() => setMetricsIsShown(true)}
          facebookMetrics={facebookMetrics}
          shopifyMetrics={shopifyMetrics}
          googleAdsMetrics={googleAdsMetrics}
          googleAnalyticsMetrics={googleAnalyticsMetrics}
          connectors={{
            shopifyConnectorId,
            magentoConnectorId,
            magentoConnectorEnabled,
            facebookConnectorId,
            googleAdsConnectorId,
            googleAnalytics4ConnectorId,
            googleSearchConsoleConnectorId,
          }}
        />

        <TableWrapper>
          {isTableLoading ? (
            <NoDataWrapper
              isshortwidth={metricsIsShown ? 'active' : ''}
              display="flex"
            >
              <TableLoader />
            </NoDataWrapper>
          ) : hasData ? (
            <CustomTable
              changeColumnOrder={handleColumnOrderChange}
              columnOrder={tableColumnOrder}
              sortConfig={tableSortConfig}
              onSortConfigChange={setTableSortConfig}
              metricsIsShown={metricsIsShown}
              rows={safeRows}
              compareDateRange={compareDateRange}
              kipifyRowsForTotals={kipifyData ?? null}
              compareData={{
                shopify:
                  compareShopifyMetricValues?.getShopifyMetricValues || null,
                magento:
                  compareMagentoMetricValues?.getMagentoMetricValues || null,
                facebook:
                  compareFacebookMetricValues?.getFacebookMetricsValues || null,
                googleAds:
                  compareGoogleAdsMetricValues?.getGoogleAdsMetrics || null,
                googleAnalytics4:
                  compareGoogleAnalyticsMetricValues?.getGoogleAnalytics4Metrics ||
                  null,
                googleSearchConsole: compareGoogleSearchConsoleData.length
                  ? compareGoogleSearchConsoleData
                  : null,
                kipify:
                  compareKipifyMetricValues?.getKipifyMetricsValues || null,
                customFormula:
                  compareCustomFormulaValues?.getCustomFormulaValues || null,
                database:
                  compareDatabaseMetricValues?.getDatabaseConnectorDataForCustomReport ||
                  null,
              }}
            />
          ) : (
            <NoDataWrapper
              isshortwidth={metricsIsShown ? 'active' : ''}
              display="flex"
              alignItems="center"
            >
              <Typography fontSize="18px" fontWeight={600} padding="20px">
                No data
              </Typography>
            </NoDataWrapper>
          )}
          {metricsIsShown && (
            <MetricsContent
              facebookData={{
                metrics: facebookMetrics,
                loading: gettingFacebookMetrics,
                hasConnector: !!facebookConnectorId,
              }}
              shopifyData={{
                metrics: shopifyMetrics,
                loading: gettingShopifyMetrics,
                hasConnector: !!shopifyConnectorId,
              }}
              magentoData={{
                metrics: magentoMetrics || [],
                loading: !!gettingMagentoMetrics,
                hasConnector: !!magentoConnectorEnabled && !!magentoConnectorId,
              }}
              googleAdsData={{
                metrics:
                  googleAdsMetrics.filter(
                    ({ category }) => category === 'metric',
                  ) || [],
                loading: gettingGoogleAdsMetrics || accountsLoading,
                hasConnector: !!googleAdsConnectorId,
                hasChoosenConnector:
                  !!googleAdsConnectorId && !!clientAccountId,
              }}
              googleAnalyticsData={{
                metrics: googleAnalyticsMetrics,
                loading:
                  gettingGoogleAnalyticsMetrics || analyticsAccountLoading,
                hasConnector: !!googleAnalytics4ConnectorId,
                hasChoosenConnector:
                  !!googleAnalytics4ConnectorId && !!propertyId,
              }}
              googleSearchConsoleData={{
                metrics: googleSearchConsoleMetrics || [],
                loading: !!gettingGoogleSearchConsoleMetrics,
                hasConnector: !!googleSearchConsoleConnectorId,
                hasChoosenConnector:
                  !!googleSearchConsoleConnectorId && !!searchConsoleSiteUrl,
              }}
              kipifyData={{
                metrics: [
                  {
                    id: 'kipify_total_marketing_spend',
                    key: 'total_marketing_spend',
                    label: 'Total Marketing Spend',
                    category: 'metric',
                    type: MetricsByConnectorType.KIPIFY,
                    valueType: ValueType.currency,
                  },
                  {
                    id: 'kipify_markup',
                    key: 'markup',
                    label: 'Markup',
                    category: 'metric',
                    type: MetricsByConnectorType.KIPIFY,
                    valueType: ValueType.number,
                  },
                  {
                    id: 'kipify_roas',
                    key: 'roas',
                    label: 'Roas',
                    category: 'metric',
                    type: MetricsByConnectorType.KIPIFY,
                    valueType: ValueType.number,
                  },
                  {
                    id: 'kipify_acos',
                    key: 'acos',
                    label: 'ACOS',
                    category: 'metric',
                    type: MetricsByConnectorType.KIPIFY,
                    valueType: ValueType.percentage,
                  },
                  {
                    id: 'kipify_acos_total',
                    key: 'acos_total',
                    label: 'ACOS (shipping incl.)',
                    category: 'metric',
                    type: MetricsByConnectorType.KIPIFY,
                    valueType: ValueType.percentage,
                  },
                  {
                    id: 'kipify_oac',
                    key: 'oac',
                    label: 'OAC',
                    category: 'metric',
                    type: MetricsByConnectorType.KIPIFY,
                    valueType: ValueType.currency,
                  },
                  {
                    id: 'kipify_cac',
                    key: 'cac',
                    label: 'CAC',
                    category: 'metric',
                    type: MetricsByConnectorType.KIPIFY,
                    valueType: ValueType.currency,
                  },
                  {
                    id: 'kipify_cac_new_customers',
                    key: 'cac_new_customers',
                    label: 'CAC (New Customers)',
                    category: 'metric',
                    type: MetricsByConnectorType.KIPIFY,
                    valueType: ValueType.currency,
                  },
                ],
                loading: false,
                hasConnector: true,
              }}
              customFormulaData={{
                metrics: transformedCustomFormulaData || [],
                loading: customFormulasLoading,
                hasConnector: true,
              }}
              expandedCollapseType={expandedMetricCollapseType}
              onToggleCollapse={toggleMetricCollapse}
              openOrCloseAllMetrics={openOrCloseAllMetrics}
            />
          )}
        </TableWrapper>

        <MainButtonGroupWrapper>
          {!hideSaveAndDeleteControls && (
            <DeleteButton onClick={handleCloseTable}>
              <DeleteIcon />
            </DeleteButton>
          )}
          <Box sx={{ display: 'flex', gap: 2, marginLeft: 'auto' }}>
            {!hideSaveAndDeleteControls && (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleSaveReport}
                disabled={savingReport}
              >
                {savingReport ? 'Saving...' : 'Save report'}
              </Button>
            )}
            <DownloadButton onClick={downloadCSV}>Download</DownloadButton>
          </Box>
        </MainButtonGroupWrapper>
      </FormProvider>
    </Wrapper>
  );
};
